import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, UserSearch, ShieldCheck, Clock, Lock, TrendingUp, Wallet, Home, Gift, Building2, ArrowUpRight, LayoutDashboard, Users, Layers, MessageCircle, Shield, Settings, CheckCircle, Key, CheckCircle2, ArrowUpDown, ArrowUp, ArrowDown, Edit, AlertCircle, FileText } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { parseTurkishNumber, formatTurkishCurrency } from "@/utils/numberUtils";
import {
  Dumbbell,
  Plus,
  Search,
  Star,
  Calendar,
  Percent,
  Banknote,
  ChevronRight,
  UserX,
  Trash2,
  LayoutGrid,
  List,
  Check,
  Crown
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import TrainerScheduleModal from "./TrainerScheduleModal";
import TrainerTimeBlockModal from "./TrainerTimeBlockModal";
import TrainerCommissionReportModal from "./TrainerCommissionReportModal";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { formatDateToLocal } from "@/lib/utils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

// Helper functions for commission type display - matching EmployeesModule format with icons
const getCommissionTypeLabel = (type, t) => {
  // Use work_type values (not commission_type)
  if (type === "salary_receiving") return <><Banknote className="w-3.5 h-3.5 text-green-600" /> {t("employees:salaryReceiving")}</>;
  if (type === "salary_and_commission_receiving") return <><Star className="w-3.5 h-3.5 text-emerald-600" /> {t("reports:salaryAndCommission")}</>;
  if (type === "commission_receiving") return <><TrendingUp className="w-3.5 h-3.5 text-blue-600" /> {t("reports:commissionReceiving")}</>;
  if (type === "commission_giving") return <><ArrowUpRight className="w-3.5 h-3.5 text-orange-600" /> {t("reports:commissionGiving")}</>;
  if (type === "rent_giving") return <><Home className="w-3.5 h-3.5 text-rose-600" /> {t("common:rentGiving")}</>;
  // Legacy commission_type values
  if (type === "percentage") return <><TrendingUp className="w-3.5 h-3.5 text-blue-600" /> {t("reports:commissionReceiving")}</>;
  if (type === "per_class") return <><TrendingUp className="w-3.5 h-3.5 text-purple-600" /> {t("common:perLessonLabel")}</>;
  if (type === "fixed") return <><Banknote className="w-3.5 h-3.5 text-green-600" /> {t("common:fixed")}</>;
  return type || t("common:notSpecified");
};

const getCommissionTypeColor = (type) => {
  if (type === "salary_receiving") return "bg-green-100 text-green-700";
  if (type === "salary_and_commission_receiving") return "bg-emerald-100 text-emerald-700";
  if (type === "commission_receiving") return "bg-blue-100 text-blue-700";
  if (type === "commission_giving") return "bg-orange-100 text-orange-700";
  if (type === "rent_giving") return "bg-rose-100 text-rose-700";
  if (type === "percentage") return "bg-blue-100 text-blue-700";
  if (type === "per_class") return "bg-purple-100 text-purple-700";
  if (type === "fixed") return "bg-green-100 text-green-700";
  return "bg-gray-100 text-gray-700";
};

export default function TrainersModule({ businessId, subscription }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "events", "packages", "employees", "admin", "surveys", "reports", "qr", "evaluations"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [trainers, setTrainers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [deleteTrainerId, setDeleteTrainerId] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "grid" veya "list"
  const [trainerCommission, setTrainerCommission] = useState(null); // Seçilen antrenörün prim verileri
  
  // Current user (for "owner-become-trainer" banner)
  const [currentUser, setCurrentUser] = useState(null);
  const [becomingTrainer, setBecomingTrainer] = useState(false);
  const [isOwnerRegistration, setIsOwnerRegistration] = useState(false);
  
  // Loading states for preventing double-click
  const [isAddingTrainer, setIsAddingTrainer] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [previousPermissions, setPreviousPermissions] = useState(null);
  
  // Invitation code system - KVKK uyumlu
  const [invitationCode, setInvitationCode] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);
  const [validatedUser, setValidatedUser] = useState(null);
  
  // Business settings for dynamic commission configuration
  const [businessTrainingTypes, setBusinessTrainingTypes] = useState([]);
  const [businessAcceptedCards, setBusinessAcceptedCards] = useState([]);
  const [businessCardPrices, setBusinessCardPrices] = useState({}); // Kart fiyatları - prim validasyonu için
  
  // Schedule and Time Block modals
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showTimeBlockModal, setShowTimeBlockModal] = useState(false);
  const [showCommissionReportModal, setShowCommissionReportModal] = useState(false);
  const [scheduleTrainer, setScheduleTrainer] = useState(null);
  const [businessWorkingHours, setBusinessWorkingHours] = useState({});
  
  // Sorting state
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Check if user can add/edit - always true for testing
  const canEdit = true; // subscription?.is_active && subscription?.plan !== 'free';

  // New trainer form
  const [trainerEmail, setTrainerEmail] = useState("");
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [commissionType, setCommissionType] = useState("percentage");
  const [commissionRates, setCommissionRates] = useState({
    one_on_one: "",
    duet: "",
    group: "",
  });
  // New commission settings for new trainer
  const [newTrainerCommissionSettings, setNewTrainerCommissionSettings] = useState({
    calculation_method: "per_lesson",
    card_commissions: {},
    package_commissions: { type: "percentage", rates: {} }
  });
  const [endReason, setEndReason] = useState("");
  const [endOtherReason, setEndOtherReason] = useState(""); // "Diğer" seçildiğinde açıklama
  
  // Work type and salary form fields
  const [workType, setWorkType] = useState("commission_receiving"); // commission_receiving, salary_receiving, salary_and_commission_receiving, commission_giving, rent_giving
  const [ownBusinessId, setOwnBusinessId] = useState(""); // Prim/Kira Veren antrenörün kendi işletmesi
  const [availableBusinesses, setAvailableBusinesses] = useState([]); // Seçilebilir işletmeler listesi
  const [startDate, setStartDate] = useState(""); // İşe Başlama Tarihi - zorunlu alan
  const [currentSalary, setCurrentSalary] = useState("");
  const [salaryStartDate, setSalaryStartDate] = useState("");
  const [newSalaryAmount, setNewSalaryAmount] = useState("");
  const [salaryEffectiveDate, setSalaryEffectiveDate] = useState("");
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryTrainer, setSalaryTrainer] = useState(null);
  const [showSalaryPaymentModal, setShowSalaryPaymentModal] = useState(false);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [salaryPaymentForm, setSalaryPaymentForm] = useState({
    trainer_id: "",
    amount: "",
    period_start: "",
    period_end: "",
    payment_date: "",
    notes: ""
  });
  
  // Commission giving fields (antrenör -> işletme)
  const [givingCommissionType, setGivingCommissionType] = useState("percentage");
  const [givingCommissionRate, setGivingCommissionRate] = useState("");
  
  // Rent fields (antrenör -> işletme)
  const [rentAmount, setRentAmount] = useState("");
  const [rentStartDate, setRentStartDate] = useState("");
  
  // Bonus payment
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusTrainer, setBonusTrainer] = useState(null);
  const [bonusForm, setBonusForm] = useState({
    amount: "",
    payment_date: "",
    reason: "",
    payment_method: "cash"
  });
  
  // User search autocomplete
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Role label mapping
  const getRoleLabel = (role) => {
    const labels = {
      trainer: t("common:trainer"),
      member: t("common:member"),
      business: t("common:business"),
      admin: t("admin:admin")
    };
    return labels[role] || role || t("common:member");
  };
  
  // Filter available businesses based on selected user's own business
  const selectedUserBusinesses = useMemo(() => {
    if (!selectedUser) return [];
    
    // Get businesses owned by the selected user
    const ownedBusinesses = availableBusinesses.filter(b => 
      b.owner_id === selectedUser.user_id || 
      selectedUser.own_business_id === b.business_id ||
      (selectedUser.has_own_business && selectedUser.own_business_id === b.business_id)
    );
    
    // Sort alphabetically
    return ownedBusinesses.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [selectedUser, availableBusinesses]);
  
  // Filter available businesses for editing trainer
  const editingTrainerBusinesses = useMemo(() => {
    if (!editingTrainer) return [];
    
    // Get the user_id of the trainer being edited
    const trainerUserId = editingTrainer.user_id;
    if (!trainerUserId) return [];
    
    // Get businesses owned by this trainer
    const ownedBusinesses = availableBusinesses.filter(b => 
      b.owner_id === trainerUserId || 
      editingTrainer.own_business_id === b.business_id
    );
    
    // Sort alphabetically
    return ownedBusinesses.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [editingTrainer, availableBusinesses]);
  
  // Debounced user search - Only trainers for trainer add modal
  const searchUsers = useCallback(async (query) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    
    setUserSearchLoading(true);
    try {
      const response = await fetch(`${API}/auth/users/search?q=${encodeURIComponent(query)}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Filter only trainer role users - members and business owners cannot be added as trainers
        const trainersOnly = data.filter(user => user.role === "trainer");
        setUserSearchResults(trainersOnly);
        setShowUserDropdown(true);
      }
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setUserSearchLoading(false);
    }
  }, []);
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery) {
        searchUsers(userSearchQuery);
      } else {
        setUserSearchResults([]);
        setShowUserDropdown(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [userSearchQuery, searchUsers]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setTrainerEmail(user.email);
    setUserSearchQuery(`${user.name || ""} ${user.surname || ""} (${user.email})`);
    setUserSearchResults([]); // Sonuçları temizle
    setShowUserDropdown(false);
    setOwnBusinessId(""); // Reset işletme seçimi
    
    // Auto-select trainer's known branches (filtered by business branches)
    const trainerKnownBranches = user.known_branches || [];
    // Filter branches: intersection of trainer's known branches and business's branches
    const businessBranchNames = branches.map(b => b.name);
    const availableForSelection = trainerKnownBranches.filter(b => businessBranchNames.includes(b));
    setSelectedBranches(availableForSelection);
  };

  useEffect(() => {
    if (businessId) {
      fetchData();
    }
  }, [businessId]);
  
  // Fetch current user once (for owner-become-trainer banner)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (r.ok) {
          const data = await r.json();
          setCurrentUser(data.user || data);
        }
      } catch (e) { /* ignore */ }
    })();
  }, []);
  
  // Open Add Trainer modal pre-filled with owner's info (no invitation code needed)
  const handleBecomeOwnerTrainer = () => {
    if (!currentUser) {
      toast.error("Kullanıcı bilgisi yüklenemedi");
      return;
    }
    // Reset everything first, then pre-fill with owner data
    resetForm();
    const ownerAsUser = {
      user_id: currentUser.user_id,
      email: currentUser.email || "",
      name: currentUser.name || "",
      surname: currentUser.surname || "",
      phone: currentUser.phone || "",
      // Owner can use all business branches
      known_branches: (branches || []).map(b => b.name),
    };
    setValidatedUser(ownerAsUser);
    setSelectedUser(ownerAsUser);
    setTrainerEmail(ownerAsUser.email);
    setIsOwnerRegistration(true);
    setShowAddModal(true);
  };

  // Fetch commission data when a trainer is selected
  useEffect(() => {
    if (selectedTrainer?.trainer_id) {
      fetchTrainerCommission(selectedTrainer.trainer_id);
    } else {
      setTrainerCommission(null);
    }
  }, [selectedTrainer]);

  const fetchTrainerCommission = async (trainerId) => {
    try {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      
      const response = await fetch(
        `${API}/trainers/${trainerId}/my-commissions?month=${month}&year=${year}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTrainerCommission(data);
      }
    } catch (error) {
      console.error("Error fetching trainer commission:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [trainersRes, businessRes, cardPricesRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/trainers`, {
          credentials: "include",
        }),
        fetch(`${API}/businesses/${businessId}`, {
          credentials: "include",
        }),
        fetch(`${API}/businesses/${businessId}/card-prices`, {
          credentials: "include",
        }),
      ]);

      if (trainersRes.ok) setTrainers(await trainersRes.json());
      if (businessRes.ok) {
        const businessData = await businessRes.json();
        setBusinessWorkingHours(businessData.working_hours || {});
        // Store training types and accepted cards for dynamic commission configuration
        setBusinessTrainingTypes(businessData.training_types || ['Private', 'Duet', 'Group']);
        setBusinessAcceptedCards(businessData.accepted_cards || []);
        
        // Use BUSINESS branches, not system branches
        const businessBranches = businessData.branches || [];
        const normalizedBranches = businessBranches.map(b => {
          if (typeof b === 'string') return { id: b, name: b, is_active: true };
          return b;
        });
        setBranches(normalizedBranches);
      }
      if (cardPricesRes.ok) {
        const cardPricesData = await cardPricesRes.json();
        // Convert array format to object format for easy lookup
        // [{card_name: "MultiSport", single_session_price: 400}] -> {MultiSport: 400}
        const cardPricesArray = cardPricesData.card_prices || [];
        const cardPricesObj = {};
        if (Array.isArray(cardPricesArray)) {
          cardPricesArray.forEach(cp => {
            if (cp.card_name) {
              cardPricesObj[cp.card_name] = cp.single_session_price || cp.price || 0;
            }
          });
        } else if (typeof cardPricesArray === 'object') {
          // Already object format
          Object.assign(cardPricesObj, cardPricesArray);
        }
        setBusinessCardPrices(cardPricesObj);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch available businesses for Prim/Kira Veren trainers
  const fetchAvailableBusinesses = async () => {
    try {
      const res = await fetch(`${API}/businesses/public/all`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        // Filter out current business
        const filtered = data.filter(b => b.business_id !== businessId);
        setAvailableBusinesses(filtered);
        return filtered; // Return the data for immediate use
      }
    } catch (error) {
      console.error("Error fetching businesses:", error);
    }
    return [];
  };
  
  useEffect(() => {
    // Fetch businesses when work type is commission_giving or rent_giving
    if (workType === "commission_giving" || workType === "rent_giving") {
      fetchAvailableBusinesses();
    }
  }, [workType]);

  const handleAddTrainer = async () => {
    // Davet kodu doğrulanmış mı kontrol et
    if (!validatedUser) {
      toast.error(t("admin:toast_onceDavetKodunuDogrulayin"));
      return;
    }
    
    if (selectedBranches.length === 0) {
      toast.error(t('common:selectAtLeastOneBranch'));
      return;
    }
    
    // İşe başlama tarihi zorunlu
    if (!startDate) {
      toast.error(t("common:toast_i̇seBaslamaTarihiZorunludur"));
      return;
    }
    
    // Prim/Kira Veren için kendi işletmesi zorunlu
    if ((workType === "commission_giving" || workType === "rent_giving") && !ownBusinessId) {
      toast.error(t("reports:toast_primkiraVerenAntrenorIcinKendi"));
      return;
    }
    
    // Validation based on work type
    if ((workType === "salary_receiving" || workType === "salary_and_commission_receiving") && !currentSalary) {
      toast.error(t("employees:toast_maasTutariZorunludur"));
      return;
    }
    if ((workType === "salary_receiving" || workType === "salary_and_commission_receiving") && !salaryStartDate) {
      toast.error(t("employees:toast_maasBaslangicTarihiZorunludur"));
      return;
    }
    if (workType === "commission_giving" && !givingCommissionRate) {
      toast.error(t("reports:toast_primOranitutariZorunludur"));
      return;
    }
    if (workType === "rent_giving" && !rentAmount) {
      toast.error(t("common:toast_kiraTutariZorunludur"));
      return;
    }
    if (workType === "rent_giving" && !rentStartDate) {
      toast.error(t("common:toast_kiraBaslangicTarihiZorunludur"));
      return;
    }
    
    // Prevent double-click
    if (isAddingTrainer) return;
    setIsAddingTrainer(true);

    try {
      // Clean commission data - convert empty strings to 0
      const cleanCommissionRates = {};
      Object.entries(commissionRates).forEach(([key, value]) => {
        cleanCommissionRates[key] = value === "" || value === null || value === undefined ? 0 : parseFloat(value) || 0;
      });
      
      // Clean commission settings - convert empty strings to 0 in nested rates
      const cleanCommissionSettings = {
        calculation_method: newTrainerCommissionSettings.calculation_method || "per_lesson",
        commission_scope: newTrainerCommissionSettings.commission_scope || "own_lessons",
        card_commissions: {},
        package_commissions: {
          type: newTrainerCommissionSettings.package_commissions?.type || "percentage",
          rates: {}
        }
      };
      
      // Clean card commissions
      if (newTrainerCommissionSettings.card_commissions) {
        Object.entries(newTrainerCommissionSettings.card_commissions).forEach(([cardName, cardConfig]) => {
          const cleanRates = {};
          if (cardConfig.rates) {
            Object.entries(cardConfig.rates).forEach(([trainingType, rate]) => {
              cleanRates[trainingType] = rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
            });
          }
          cleanCommissionSettings.card_commissions[cardName] = {
            type: cardConfig.type || "percentage",
            rates: cleanRates
          };
        });
      }
      
      // Clean package commissions
      if (newTrainerCommissionSettings.package_commissions?.rates) {
        Object.entries(newTrainerCommissionSettings.package_commissions.rates).forEach(([trainingType, rate]) => {
          cleanCommissionSettings.package_commissions.rates[trainingType] = 
            rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
        });
      }
      
      const endpoint = isOwnerRegistration
        ? `${API}/businesses/${businessId}/owner-become-trainer`
        : `${API}/trainers/add-by-email`;
      const body = isOwnerRegistration
        ? {
            branches: selectedBranches,
            commission_type: commissionType,
            commission_rates: cleanCommissionRates,
            commission_settings: cleanCommissionSettings,
            work_type: workType,
            own_business_id: (workType === "commission_giving" || workType === "rent_giving") ? ownBusinessId : null,
            start_date: startDate,
            current_salary: parseFloat(currentSalary) || 0,
            salary_start_date: salaryStartDate || null,
            giving_commission_type: givingCommissionType,
            giving_commission_rate: parseFloat(givingCommissionRate) || 0,
            rent_amount: parseFloat(rentAmount) || 0,
            rent_start_date: rentStartDate || null,
          }
        : {
            user_id: validatedUser.user_id, // <-- En kritik alan: Doğrudan user_id gönderilmeli
            phone: validatedUser.phone || null,
            email: validatedUser.email || null,
            business_id: businessId,
            branches: selectedBranches,
            commission_type: commissionType,
            commission_rates: cleanCommissionRates,
            commission_settings: cleanCommissionSettings,
            work_type: workType,
            own_business_id: (workType === "commission_giving" || workType === "rent_giving") ? ownBusinessId : null,
            start_date: startDate,
            current_salary: parseFloat(currentSalary) || 0,
            salary_start_date: salaryStartDate || null,
            giving_commission_type: givingCommissionType,
            giving_commission_rate: parseFloat(givingCommissionRate) || 0,
            rent_amount: parseFloat(rentAmount) || 0,
            rent_start_date: rentStartDate || null,
          };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success(t("common:toast_antrenorBasariylaEklendi"));
        setShowAddModal(false);
        resetForm();
        // Reset commission settings
        setNewTrainerCommissionSettings({
          calculation_method: "per_lesson",
          card_commissions: {},
          package_commissions: { type: "percentage", rates: {} }
        });
        fetchData();
      } else {
        const error = await response.json();
        // Handle Pydantic validation errors (array format)
        let errorMessage = t("common:trainerCouldNotBeAdded");
        if (error.detail) {
          if (typeof error.detail === "string") {
            errorMessage = error.detail;
          } else if (Array.isArray(error.detail)) {
            // Pydantic validation error format
            errorMessage = error.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
          } else if (typeof error.detail === "object") {
            errorMessage = error.detail.msg || error.detail.message || JSON.stringify(error.detail);
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsAddingTrainer(false);
    }
  };

  const handleEndTrainer = async () => {
    if (!selectedTrainer || !endReason) {
      toast.error(t("common:toast_ayrilmaNedeniZorunludur"));
      return;
    }
    
    // "Diğer" seçilmişse açıklama zorunlu
    if (endReason === "diger" && !endOtherReason.trim()) {
      toast.error(t("common:toast_lutfenAyrilmaNedeniniAciklayin"));
      return;
    }

    try {
      // Reason olarak "diger" seçilmişse açıklamayı da ekle
      const reasonToSend = endReason === "diger" 
        ? `${t("common:other")}: ${endOtherReason.trim()}` 
        : endReason;
      
      const response = await fetch(`${API}/trainers/${selectedTrainer.trainer_id}/end`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          reason: reasonToSend,
          other_reason_detail: endReason === "diger" ? endOtherReason.trim() : null
        }),
      });

      if (response.ok) {
        toast.success(t("common:toast_antrenorAyrildiOlarakIsaretlendi"));
        fetchData();
        setShowEndModal(false);
        setSelectedTrainer(null);
        setEndReason("");
        setEndOtherReason("");
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:trainerLeaveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Toggle trainer active/inactive status
  const handleToggleStatus = async (trainer, e) => {
    if (e) e.stopPropagation();
    
    const newStatus = (trainer.status === "active" || trainer.is_active) ? "inactive" : "active";
    const action = newStatus === "inactive" ? "pasif" : "aktif";
    
    try {
      const response = await fetch(`${API}/trainers/${trainer.trainer_id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success(t("common:trainerActionSuccess"));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:trainerActionFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Edit trainer - open edit modal
  const handleEditTrainer = (trainer, e) => {
    if (e) e.stopPropagation();
    openEditModal(trainer);
  };

  const resetForm = () => {
    setTrainerEmail("");
    setSelectedBranches([]);
    setCommissionType("percentage");
    setCommissionRates({ one_on_one: "", duet: "", group: "" });
    setUserSearchQuery("");
    setUserSearchResults([]);
    setSelectedUser(null);
    setShowUserDropdown(false);
    // Reset work type fields
    setWorkType("commission_receiving");
    setOwnBusinessId(""); // Reset kendi işletmesi
    setStartDate(""); // Reset işe başlama tarihi
    setCurrentSalary("");
    setSalaryStartDate("");
    setGivingCommissionType("percentage");
    setGivingCommissionRate("");
    setRentAmount("");
    setRentStartDate("");
    // Reset invitation code
    setInvitationCode("");
    setValidatedUser(null);
    setIsOwnerRegistration(false);
  };
  
  // Davet kodu doğrulama (KVKK uyumlu sistem)
  const validateInvitationCode = async () => {
    if (!invitationCode || invitationCode.length !== 6) {
      toast.error(t("admin:enterSixDigitCode"));
      return;
    }
    
    setValidatingCode(true);
    try {
      const response = await fetch(`${API}/invitation/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: invitationCode }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidatedUser(data.user);
        setTrainerEmail(data.user.email || "");
        setSelectedUser({
          user_id: data.user.user_id,
          email: data.user.email,
          name: data.user.name,
          surname: data.user.surname,
          phone: data.user.phone,
          known_branches: data.user.known_branches || []
        });
        toast.success(t('common:userVerified'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:invalidOrExpiredCode"));
        setValidatedUser(null);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
      setValidatedUser(null);
    } finally {
      setValidatingCode(false);
    }
  };
  
  // Bonus payment handlers
  const openBonusModal = (trainer) => {
    setBonusTrainer(trainer);
    setBonusForm({
      amount: "",
      payment_date: formatDateToLocal(new Date()),
      reason: "",
      payment_method: "cash"
    });
    setShowBonusModal(true);
  };

  const handleBonusPayment = async () => {
    if (!bonusTrainer || !bonusForm.amount) {
      toast.error("Tutar giriniz");
      return;
    }

    try {
      const response = await fetch(`${API}/trainers/${bonusTrainer.trainer_id}/bonus-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: parseFloat(bonusForm.amount),
          payment_date: bonusForm.payment_date,
          reason: bonusForm.reason,
          payment_method: bonusForm.payment_method
        })
      });

      if (response.ok) {
        toast.success(t('payments:additionalPaymentSaved'));
        setShowBonusModal(false);
        setBonusTrainer(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:couldNotSavePayment"));
      }
    } catch (error) {
      console.error("Bonus payment error:", error);
      toast.error(t("common:couldNotSavePayment"));
    }
  };
  
  const openEditModal = async (trainer) => {
    // Fetch trainer's user profile to get known_branches
    let trainerKnownBranches = [];
    try {
      const userResponse = await fetch(`${API}/auth/users/search?q=${encodeURIComponent(trainer.email)}`, {
        credentials: "include"
      });
      if (userResponse.ok) {
        const users = await userResponse.json();
        const trainerUser = users.find(u => u.email === trainer.email);
        if (trainerUser) {
          trainerKnownBranches = trainerUser.known_branches || [];
        }
      }
    } catch (error) {
      console.error("Error fetching trainer user:", error);
    }
    
    // If trainer is commission_giving or rent_giving, fetch available businesses and use returned data
    let fetchedBusinesses = [];
    if (['commission_giving', 'rent_giving'].includes(trainer.work_type)) {
      fetchedBusinesses = await fetchAvailableBusinesses();
    }
    
    // Find business name if own_business_id exists - use freshly fetched data first
    let ownBusinessName = trainer.own_business_name || "";  // Backend'den geliyorsa kullan
    if (trainer.own_business_id && !ownBusinessName) {
      // First try from freshly fetched businesses
      let biz = fetchedBusinesses.find(b => b.business_id === trainer.own_business_id);
      
      // If not found in fetched data, try from current state
      if (!biz) {
        biz = availableBusinesses.find(b => b.business_id === trainer.own_business_id);
      }
      
      // If still not found, fetch the business directly
      if (!biz) {
        try {
          const res = await fetch(`${API}/businesses/${trainer.own_business_id}`, {
            credentials: "include"
          });
          if (res.ok) {
            const bizData = await res.json();
            ownBusinessName = bizData.name || trainer.own_business_id;
          }
        } catch (e) {
          console.error("Error fetching business name:", e);
        }
      } else {
        ownBusinessName = biz.name;
      }
    }
    
    setPreviousPermissions(null);
    setEditingTrainer({
      ...trainer,
      name: trainer.name || "",
      surname: trainer.surname || "",
      branches: trainer.branches || [],
      known_branches: trainerKnownBranches,  // Add known branches from user profile
      commission_type: trainer.commission_type || "percentage",
      commission_rates: trainer.commission_rates || { one_on_one: "0", duet: "0", group: "0" },
      commission_settings: trainer.commission_settings || null,
      // Work type settings
      work_type: trainer.work_type || trainer.salary_type || "commission_receiving",  // Backward compat with salary_type
      own_business_id: trainer.own_business_id || "",  // Kendi işletmesi
      own_business_name: ownBusinessName,  // İşletme adı
      giving_commission_type: trainer.giving_commission_type || "percentage",
      giving_commission_rate: trainer.giving_commission_rate || "",
      rent_amount: trainer.rent_amount || "",
      rent_start_date: trainer.rent_start_date || "",
      is_manager: trainer.is_manager || false,
      is_co_owner: trainer.is_co_owner || false,
      permissions: trainer.permissions || {},
      can_approve_reservations: trainer.can_approve_reservations || false,
    });
    setShowEditModal(true);
  };
  
  const handleUpdateTrainer = async () => {
    if (!editingTrainer) return;
    
    // Validate branches - at least one required
    if (!editingTrainer.branches || editingTrainer.branches.length === 0) {
      toast.error(t('common:selectAtLeastOneBranch'));
      return;
    }
    
    // Prevent double-click
    if (isSavingEdit) return;
    setIsSavingEdit(true);
    
    try {
      // Clean commission rates - convert empty strings to 0
      const cleanCommissionRates = {};
      if (editingTrainer.commission_rates) {
        Object.entries(editingTrainer.commission_rates).forEach(([key, value]) => {
          cleanCommissionRates[key] = value === "" || value === null || value === undefined ? 0 : parseFloat(value) || 0;
        });
      }
      
      // Clean commission settings - convert empty strings to 0 in nested rates
      const rawSettings = editingTrainer.commission_settings || {};
      const cleanCommissionSettings = {
        calculation_method: rawSettings.calculation_method || "per_lesson",
        commission_scope: rawSettings.commission_scope || "own_lessons",
        card_commissions: {},
        package_commissions: {
          type: rawSettings.package_commissions?.type || "percentage",
          rates: {}
        }
      };
      
      // Clean card commissions
      if (rawSettings.card_commissions) {
        Object.entries(rawSettings.card_commissions).forEach(([cardName, cardConfig]) => {
          const cleanRates = {};
          if (cardConfig.rates) {
            Object.entries(cardConfig.rates).forEach(([trainingType, rate]) => {
              cleanRates[trainingType] = rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
            });
          }
          cleanCommissionSettings.card_commissions[cardName] = {
            type: cardConfig.type || "percentage",
            rates: cleanRates
          };
        });
      }
      
      // Clean package commissions
      if (rawSettings.package_commissions?.rates) {
        Object.entries(rawSettings.package_commissions.rates).forEach(([trainingType, rate]) => {
          cleanCommissionSettings.package_commissions.rates[trainingType] = 
            rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
        });
      }
      
      // Build update payload with new commission system
      const updateData = {
        name: editingTrainer.name,
        surname: editingTrainer.surname,
        branches: editingTrainer.branches,
        commission_type: editingTrainer.commission_type,
        commission_rates: cleanCommissionRates,
        // Work type settings
        work_type: editingTrainer.work_type || "commission_receiving",
        // Kendi işletmesi (Prim/Kira Veren için)
        own_business_id: ['commission_giving', 'rent_giving'].includes(editingTrainer.work_type) 
          ? editingTrainer.own_business_id : null,
        // İşe başlama tarihi
        start_date: editingTrainer.start_date,
        // Commission giving settings (antrenör -> işletme)
        giving_commission_type: editingTrainer.giving_commission_type,
        giving_commission_rate: parseFloat(editingTrainer.giving_commission_rate) || 0,
        // Manager and permissions
        is_manager: editingTrainer.is_manager || false,
        is_co_owner: editingTrainer.is_co_owner || false,
        permissions: editingTrainer.permissions || {},
        // Doğrudan rezervasyon onayı yetkisi
        can_approve_reservations: editingTrainer.can_approve_reservations || false,
      };
      
      // Send commission_settings for ALL work types that can receive commission
      // (commission_receiving, salary_and_commission_receiving, commission_giving, rent_giving)
      // commission_giving and rent_giving trainers also receive commission when working with business's OWN members
      const commissionReceivingTypes = ['commission_receiving', 'salary_and_commission_receiving', 'commission_giving', 'rent_giving'];
      if (commissionReceivingTypes.includes(editingTrainer.work_type)) {
        updateData.commission_settings = cleanCommissionSettings;
      }
      // NOTE: Only salary_receiving doesn't get commission_settings (no commission at all)
      
      // Add rent update if applicable
      if (editingTrainer.work_type === 'rent_giving' && editingTrainer.rent_amount) {
        updateData.rent_amount = parseFloat(editingTrainer.rent_amount) || 0;
        updateData.rent_start_date = editingTrainer.rent_start_date;
      }
      
      // Add salary change if provided
      if (newSalaryAmount && salaryEffectiveDate) {
        updateData.new_salary = parseTurkishNumber(newSalaryAmount);
        updateData.salary_effective_date = salaryEffectiveDate;
      }
      
      const response = await fetch(`${API}/trainers/${editingTrainer.trainer_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData),
      });
      
      if (response.ok) {
        toast.success(t('trainers:trainerUpdated'));
        setShowEditModal(false);
        setEditingTrainer(null);
        setSelectedTrainer(null);
        setNewSalaryAmount("");
        setSalaryEffectiveDate("");
        fetchData();
      } else {
        const error = await response.json();
        // Handle Pydantic validation errors (array format)
        let errorMessage = t('settings:updateFailed');
        if (error.detail) {
          if (typeof error.detail === "string") {
            errorMessage = error.detail;
          } else if (Array.isArray(error.detail)) {
            errorMessage = error.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
          } else if (typeof error.detail === "object") {
            errorMessage = error.detail.msg || error.detail.message || JSON.stringify(error.detail);
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSavingEdit(false);
    }
  };
  
  const handleToggleManager = async (trainerId, isManager, e) => {
    e?.stopPropagation(); // Satır tıklamasını engelle
    try {
      const response = await fetch(`${API}/trainers/${trainerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_manager: isManager }),
      });
      
      if (response.ok) {
        toast.success(isManager ? t("employees:managerPermissionGranted") : t("employees:managerPermissionRevoked"));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const toggleBranch = (branchName) => {
    setSelectedBranches((prev) =>
      prev.includes(branchName)
        ? prev.filter((b) => b !== branchName)
        : [...prev, branchName]
    );
  };

  const handleDeleteTrainer = (trainerId, e) => {
    e.stopPropagation(); // Satır tıklamasını engelle
    setDeleteTrainerId(trainerId); // Modal aç
  };
  
  const confirmDeleteTrainer = async () => {
    if (!deleteTrainerId) return;
    
    try {
      const response = await fetch(`${API}/trainers/${deleteTrainerId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(t('trainers:trainerDeleted'));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('members:deleteFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setDeleteTrainerId(null);
    }
  };

  const filteredTrainers = trainers.filter((t) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      t.name?.toLowerCase().includes(query) ||
      t.surname?.toLowerCase().includes(query)
    );
  });

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortDirection === "asc" ? 
      <ArrowUp className="w-4 h-4 ml-1" /> : 
      <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Sort filtered trainers
  const sortedTrainers = [...filteredTrainers].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case "name":
        aVal = `${a.name} ${a.surname}`.toLowerCase();
        bVal = `${b.name} ${b.surname}`.toLowerCase();
        break;
      case "work_type":
        aVal = a.work_type || "";
        bVal = b.work_type || "";
        break;
      case "start_date":
        aVal = a.start_date || "";
        bVal = b.start_date || "";
        break;
      case "status":
        aVal = (a.status === "active" || a.is_active) ? "active" : "inactive";
        bVal = (b.status === "active" || b.is_active) ? "active" : "inactive";
        break;
      default:
        aVal = "";
        bVal = "";
    }
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const getCommissionDisplay = (trainer) => {
    if (trainer.commission_type === "percentage") {
      return `%${trainer.commission_rates?.one_on_one || 0}`;
    }
    return `${currencySymbol}${trainer.commission_rates?.one_on_one || 0}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Owner-Become-Trainer Banner — sadece işletme sahibi ve henüz antrenör değilse */}
      {currentUser?.role === "business" &&
       trainers.length >= 0 &&
       !trainers.some(t => t.user_id === currentUser?.user_id) && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="owner-become-trainer-banner">
          <div className="flex items-start gap-3">
            <div className="bg-purple-100 rounded-full p-2 flex-shrink-0">
              <UserSearch className="w-5 h-5 text-purple-700" />
            </div>
            <div>
              <p className="font-semibold text-navy">Siz aynı zamanda kendi işletmenizde antrenörlük yapıyor musunuz?</p>
              <p className="text-xs text-grey mt-0.5">Aşağıdaki butona tıklayarak Antrenör Ekle formunu açın; şube, çalışma şekli ve prim detaylarınızı belirleyip kaydedin. (E-posta/telefon değişmez.)</p>
            </div>
          </div>
          <Button
            onClick={handleBecomeOwnerTrainer}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl flex-shrink-0"
            data-testid="owner-become-trainer-btn"
          >
            Beni Antrenör Olarak Ekle
          </Button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy">{t('common:trainers')}</h2>
          <p className="text-grey text-sm">{t("common:trainerCount", { count: trainers.length })}</p>
        </div>
        <div className="flex gap-2">
          {/* Grid/List Toggle - Two separate buttons (same as EmployeesModule) */}
          <div className="flex border rounded-xl overflow-hidden">
            <Button 
              variant={viewMode === "list" ? "default" : "ghost"} 
              size="sm" 
              onClick={() => setViewMode("list")} 
              className={`rounded-none ${viewMode === "list" ? "bg-navy text-white" : ""}`}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === "grid" ? "default" : "ghost"} 
              size="sm" 
              onClick={() => setViewMode("grid")} 
              className={`rounded-none ${viewMode === "grid" ? "bg-navy text-white" : ""}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>

          {canEdit && (
            <Dialog open={showAddModal} onOpenChange={(open) => {
              if (!open) {
                resetForm();
              }
              setShowAddModal(open);
            }}>
              <Button
                className="bg-navy hover:bg-navy/90 rounded-xl"
                data-testid="add-trainer-btn"
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("common:newTrainer")}
              </Button>
            <DialogContent className="max-w-2xl w-[95vw] sm:w-auto max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {isOwnerRegistration ? "Kendinizi Antrenör Olarak Ekleyin" : t('trainers:addNewTrainer')}
                </DialogTitle>
                <p className="text-sm text-gray-500">
                  {isOwnerRegistration
                    ? "İşletme sahibi olarak antrenörlük detaylarınızı (şubeler, çalışma şekli, prim oranları) belirleyin."
                    : t("admin:trainerInviteCodeDesc")}
                </p>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Invitation Code Input - KVKK Uyumlu (gizli: owner kendisini ekliyorsa) */}
                <div className="space-y-2" style={{ display: isOwnerRegistration ? 'none' : undefined }}>
                  <Label className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    {t("admin:invitationCode")}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t("admin:sixDigitCode2")}
                      value={invitationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setInvitationCode(value);
                        if (value.length < 6) {
                          setValidatedUser(null);
                          setSelectedUser(null);
                          setTrainerEmail("");
                        }
                      }}
                      className="text-center text-xl font-mono tracking-[0.3em] rounded-xl"
                      maxLength={6}
                      disabled={validatedUser !== null}
                      data-testid="trainer-invitation-code-input"
                    />
                    {!validatedUser ? (
                      <Button
                        onClick={validateInvitationCode}
                        disabled={invitationCode.length !== 6 || validatingCode}
                        className="bg-navy hover:bg-navy/90 rounded-xl px-6"
                        data-testid="validate-trainer-code-btn"
                      >
                        {validatingCode ? "..." : t('members:verify')}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setValidatedUser(null);
                          setInvitationCode("");
                          setTrainerEmail("");
                          setSelectedUser(null);
                        }}
                        className="rounded-xl px-4"
                      >
                        {t("common:change")}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {t("admin:trainerCanGetCode")}
                  </p>
                </div>
                
                {/* Validated User Display */}
                {validatedUser && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                      {validatedUser.name?.[0]}{validatedUser.surname?.[0]}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-green-800">
                        {validatedUser.name} {validatedUser.surname}
                      </p>
                      <p className="text-sm text-green-600">{validatedUser.email}</p>
                      {validatedUser.phone && (
                        <p className="text-xs text-green-600">{validatedUser.phone}</p>
                      )}
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                )}

                <div>
                  <Label>{t('common:branches')}</Label>
                  {selectedUser ? (
                    <>
                      <p className="text-xs text-grey mb-2">
                        {t("employees:branchIntersection")}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {/* Get trainer's known branches */}
                        {(() => {
                          const trainerKnownBranches = selectedUser.known_branches || [];
                          // Filter business branches to only show trainer's known branches (intersection)
                          const availableBranches = branches.filter(b => 
                            trainerKnownBranches.includes(b.name)
                          );
                          
                          if (availableBranches.length === 0) {
                            return (
                              <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                                <p className="font-medium">⚠️ {t("common:branchMismatch")}</p>
                                <p className="mt-1">{t("common:businessBranches", { branches: branches.length > 0 ? branches.map(b => b.name).join(", ") : t("common:none2") })}</p>
                                <p>{t("common:trainerKnownBranches2", { branches: trainerKnownBranches.length > 0 ? trainerKnownBranches.join(", ") : t("common:none2") })}</p>
                                <p className="mt-1 text-amber-900">{t("common:noCommonBranch")}</p>
                              </div>
                            );
                          }
                          
                          return availableBranches.map((branch) => (
                            <button
                              key={branch.id}
                              type="button"
                              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                                selectedBranches.includes(branch.name)
                                  ? "bg-navy text-white"
                                  : "bg-grey-100 text-grey-700 hover:bg-grey-200"
                              }`}
                              onClick={() => toggleBranch(branch.name)}
                            >
                              {getLocalizedBranch(branch.name)}
                            </button>
                          ));
                        })()}
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2 mt-2 opacity-50">
                      {branches.map((branch) => (
                        <button
                          key={branch.id}
                          type="button"
                          disabled
                          className="px-3 py-1.5 rounded-full text-sm bg-grey-100 text-grey-400 cursor-not-allowed"
                        >
                          {getLocalizedBranch(branch.name)}
                        </button>
                      ))}
                      <p className="w-full text-xs text-grey mt-1">
                        {t("common:selectTrainerFirst")}
                      </p>
                    </div>
                  )}
              </div>

              {/* İşe Başlama Tarihi - Zorunlu Alan */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-navy" />
                  {t("common:startDate2")}
                </Label>
                <DatePickerTR
                  value={startDate ? new Date(startDate) : null}
                  onChange={(date) => setStartDate(date ? formatDateToLocal(date) : "")}
                  placeholder={t('common:selectDate')}
                  className="rounded-xl"
                  data-testid="add-trainer-start-date"
                />
                <p className="text-xs text-grey">
                  {t("common:trainerStartDateNote")}
                </p>
              </div>

              {/* Çalışma Şekli - Add Modal */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-semibold text-navy flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  {t("common:workType")}
                </h4>
                <Select
                  value={workType}
                  onValueChange={(value) => {
                    setWorkType(value);
                    // Reset related fields when changing work type
                    if (!['salary_receiving', 'salary_and_commission_receiving'].includes(value)) {
                      setCurrentSalary("");
                      setSalaryStartDate("");
                    }
                    if (value !== 'commission_giving') {
                      setGivingCommissionRate("");
                    }
                    if (value !== 'rent_giving') {
                      setRentAmount("");
                      setRentStartDate("");
                    }
                  }}
                >
                  <SelectTrigger className="rounded-xl" data-testid="add-trainer-work-type">
                    <SelectValue placeholder={t("common:ph_calismaSekliSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commission_receiving" className="text-green-700">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">💰</span> {t("reports:commissionReceiving")}
                      </div>
                    </SelectItem>
                    <SelectItem value="salary_receiving" className="text-green-700">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">💵</span> {t("employees:salaryReceiving")}
                      </div>
                    </SelectItem>
                    <SelectItem value="salary_and_commission_receiving" className="text-green-700">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">💎</span> {t("reports:salaryAndCommission")}
                      </div>
                    </SelectItem>
                    <SelectItem disabled value="separator" className="opacity-30">
                      ─────────────────
                    </SelectItem>
                    <SelectItem value="commission_giving" className="text-orange-700">
                      <div className="flex items-center gap-2">
                        <span className="text-orange-600">📤</span> {t("reports:commissionGiving")}
                      </div>
                    </SelectItem>
                    <SelectItem value="rent_giving" className="text-orange-700">
                      <div className="flex items-center gap-2">
                        <span className="text-orange-600">🏠</span> {t("common:rentGiving")}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Work Type Explanation */}
                <div className={`p-3 rounded-xl text-sm ${
                  ['commission_receiving', 'salary_receiving', 'salary_and_commission_receiving'].includes(workType)
                    ? 'bg-green-50 text-green-800'
                    : 'bg-orange-50 text-orange-800'
                }`}>
                  {workType === 'commission_receiving' && (
                    <p>💰 {t("reports:perLessonCommDesc")}</p>
                  )}
                  {workType === 'salary_receiving' && (
                    <p>💵 {t("reports:salaryOnlyDesc")}</p>
                  )}
                  {workType === 'salary_and_commission_receiving' && (
                    <p>💎 {t("reports:salaryPlusCommDesc")}</p>
                  )}
                  {workType === 'commission_giving' && (
                    <p>📤 {t("reports:revenueShareDesc")}</p>
                  )}
                  {workType === 'rent_giving' && (
                    <p>🏠 {t("reports:rentDesc")}</p>
                  )}
                </div>
                
                {/* Kendi İşletmesi Seçimi - Prim/Kira Veren için */}
                {(workType === 'commission_giving' || workType === 'rent_giving') && (
                  <div className="border-t pt-4 space-y-3">
                    <h4 className="font-semibold text-orange-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {t("common:ownBusiness")} <span className="text-red-500">*</span>
                    </h4>
                    <p className="text-xs text-grey">{t("common:antrenorunDisUyeIleDers")}</p>
                    <Select 
                      value={ownBusinessId} 
                      onValueChange={setOwnBusinessId}
                      disabled={!selectedUser}
                    >
                      <SelectTrigger className="rounded-xl border-orange-300">
                        <SelectValue placeholder={selectedUser ? t("common:selectBusinessPlaceholder") : t("common:selectTrainerFirstPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {!selectedUser ? (
                          <SelectItem value="no-user" disabled>
                            {t("common:selectTrainerFirstPlaceholder")}
                          </SelectItem>
                        ) : selectedUserBusinesses.length > 0 ? (
                          selectedUserBusinesses.map(b => (
                            <SelectItem key={b.business_id} value={b.business_id}>
                              {b.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-business" disabled>
                            {t("common:trainerHasNoBusiness")}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {!selectedUser && (
                      <p className="text-xs text-amber-600">ℹ️ {t("common:selectTrainerFirstNote")}</p>
                    )}
                    {selectedUser && selectedUserBusinesses.length === 0 && (
                      <p className="text-xs text-red-500">⚠️ {t("admin:noRegisteredBusiness")}</p>
                    )}
                    {selectedUser && selectedUserBusinesses.length > 0 && !ownBusinessId && (
                      <p className="text-xs text-red-500">⚠️ {t("admin:businessRequiredForExternal")}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Maaş Ayarları - salary_receiving veya salary_and_commission_receiving için */}
              {(workType === 'salary_receiving' || workType === 'salary_and_commission_receiving') && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-semibold text-navy flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    {t("employees:salarySettings")} <span className="text-red-500">*</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">{t("employees:salaryAmount")}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={currentSalary}
                        onChange={(e) => setCurrentSalary(e.target.value)}
                        placeholder="0"
                        className="rounded-xl"
                        data-testid="add-trainer-salary-amount"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">{t("common:startDateRequired")}</Label>
                      <DatePickerTR
                        value={salaryStartDate ? new Date(salaryStartDate) : null}
                        onChange={(date) => setSalaryStartDate(date ? formatDateToLocal(date) : "")}
                        placeholder={t('common:selectDate')}
                        data-testid="add-trainer-salary-date"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Prim Verme Ayarları - commission_giving için */}
              {workType === 'commission_giving' && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-semibold text-orange-700 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    {t("common:shareToGiveBusiness")} <span className="text-red-500">*</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Tip</Label>
                      <Select value={givingCommissionType} onValueChange={setGivingCommissionType}>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                          <SelectItem value="fixed">{t("common:fixed")} ({currencySymbol})</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">
                        {givingCommissionType === 'percentage' ? 'Oran (%)' : 'Tutar ({currencySymbol})'} *
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        max={givingCommissionType === 'percentage' ? "100" : undefined}
                        step={givingCommissionType === 'percentage' ? "1" : "0.01"}
                        value={givingCommissionRate}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Yüzde tipi için %100 sınırı
                          if (givingCommissionType === 'percentage') {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue) && numValue > 100) {
                              value = "100";
                            }
                          }
                          setGivingCommissionRate(value);
                        }}
                        onBlur={(e) => {
                          // Blur olduğunda da kontrol et
                          if (givingCommissionType === 'percentage') {
                            const numValue = parseFloat(e.target.value);
                            if (!isNaN(numValue) && numValue > 100) {
                              setGivingCommissionRate("100");
                            }
                          }
                        }}
                        placeholder={givingCommissionType === 'percentage' ? "20" : "100"}
                        className="rounded-xl"
                        data-testid="add-trainer-giving-rate"
                      />
                      {givingCommissionType === 'percentage' && parseFloat(givingCommissionRate) > 100 && (
                        <p className="text-xs text-red-500 mt-1">⚠️ {t("reports:commissionRateCannotExceed100")}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-orange-600">
                    {t("common:trainerOwesPerLesson")}
                  </p>
                </div>
              )}

              {/* Kira Ayarları - rent_giving için */}
              {workType === 'rent_giving' && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-semibold text-orange-700 flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    {t("common:rentSettings")} <span className="text-red-500">*</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">{t("common:monthlyRent")}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={rentAmount}
                        onChange={(e) => setRentAmount(e.target.value)}
                        placeholder="5000"
                        className="rounded-xl"
                        data-testid="add-trainer-rent-amount"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">{t("common:startDateRequired")}</Label>
                      <DatePickerTR
                        value={rentStartDate ? new Date(rentStartDate) : null}
                        onChange={(date) => setRentStartDate(date ? formatDateToLocal(date) : "")}
                        placeholder={t('common:selectDate')}
                        data-testid="add-trainer-rent-date"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-orange-600">
                    {t("common:trainerOwesMonthlyRent")}
                  </p>
                </div>
              )}

              {/* Prim Ayarları - Prim alan tipler için (Maaş Alan HARİÇ) */}
              {['commission_receiving', 'salary_and_commission_receiving', 'commission_giving', 'rent_giving'].includes(workType) && (
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-navy flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  {workType === 'commission_giving' || workType === 'rent_giving' 
                    ? t('reports:commissionSettingsForBizMembers') 
                    : t('reports:commissionSettingsForTrainer')}
                </h4>
                
                {(workType === 'commission_giving' || workType === 'rent_giving') && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    ⚠️ {t("reports:commissionSettingsNote")}
                  </p>
                )}
                
                {/* Prim Hesaplama Yöntemi */}
                <div className="p-3 bg-blue-50 rounded-xl space-y-2">
                  <Label className="font-medium text-blue-800 text-sm">{t('trainers:commissionCalculation')}</Label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border hover:border-blue-300 text-sm">
                      <input
                        type="radio"
                        name="newCalculationMethod"
                        checked={newTrainerCommissionSettings.calculation_method !== "per_package_sale"}
                        onChange={() => setNewTrainerCommissionSettings(prev => ({
                          ...prev,
                          calculation_method: "per_lesson"
                        }))}
                        className="mt-0.5"
                      />
                      <div>
                        <p className="font-medium">{t("common:perLessonLabel")}</p>
                        <p className="text-xs text-grey">{t("reports:separateCommPerLesson")}</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border hover:border-blue-300 text-sm">
                      <input
                        type="radio"
                        name="newCalculationMethod"
                        checked={newTrainerCommissionSettings.calculation_method === "per_package_sale"}
                        onChange={() => setNewTrainerCommissionSettings(prev => ({
                          ...prev,
                          calculation_method: "per_package_sale"
                        }))}
                        className="mt-0.5"
                      />
                      <div>
                        <p className="font-medium">📦 {t("packages:perPackageSale")}</p>
                        <p className="text-xs text-grey">{t("packages:totalCommOnPackageSale")}</p>
                      </div>
                    </label>
                    {newTrainerCommissionSettings.calculation_method === "per_package_sale" && (
                      <div className="ml-6 p-3 bg-blue-100 rounded-lg space-y-2">
                        <Label className="text-xs font-medium text-blue-800">{t("reports:commissionScope")}</Label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            name="newCommissionScope"
                            checked={newTrainerCommissionSettings.commission_scope !== "all_lessons"}
                            onChange={() => setNewTrainerCommissionSettings(prev => ({
                              ...prev,
                              commission_scope: "own_lessons"
                            }))}
                          />
                          <span>Kendi derslerinden</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            name="newCommissionScope"
                            checked={newTrainerCommissionSettings.commission_scope === "all_lessons"}
                            onChange={() => setNewTrainerCommissionSettings(prev => ({
                              ...prev,
                              commission_scope: "all_lessons"
                            }))}
                          />
                          <span>{t("common:fromAllLessons")}</span>
                        </label>
                        <p className="text-xs text-blue-700">
                          {newTrainerCommissionSettings.commission_scope === "all_lessons"
                            ? t("packages:allPackageSalesComm")
                            : t("packages:ownPackageSalesComm")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* {t("reports:cardCommissions")} */}
                {businessAcceptedCards.length > 0 && (
                  <div className="space-y-2">
                    <Label className="font-medium text-amber-800 text-sm flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      {t("reports:cardCommissions")}
                    </Label>
                    {businessAcceptedCards.map(cardName => {
                      const cardConfig = newTrainerCommissionSettings.card_commissions?.[cardName] || { type: "percentage", rates: {} };
                      return (
                        <div key={cardName} className="p-3 bg-amber-50 rounded-xl space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-amber-700 text-sm">{cardName}</span>
                            <Select 
                              value={cardConfig.type || "percentage"} 
                              onValueChange={(v) => setNewTrainerCommissionSettings(prev => ({
                                ...prev,
                                card_commissions: {
                                  ...prev.card_commissions,
                                  [cardName]: { ...cardConfig, type: v }
                                }
                              }))}
                            >
                              <SelectTrigger className="w-28 h-8 rounded-lg text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                                <SelectItem value="fixed">{t("common:fixed")} ({currencySymbol})</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Kart fiyatı uyarısı - sabit prim için */}
                          {cardConfig.type === "fixed" && (!businessCardPrices[cardName] || businessCardPrices[cardName] <= 0) && (
                            <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                              ⚠️ {t("common:cardPriceNotSet")}
                            </p>
                          )}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {businessTrainingTypes.map(trainingType => {
                              const cardPrice = businessCardPrices[cardName] || 0;
                              const maxFixedValue = cardConfig.type === "fixed" ? cardPrice : undefined;
                              const currentValue = cardConfig.rates?.[trainingType] || "";
                              const isOverMax = cardConfig.type === "fixed" && cardPrice > 0 && parseFloat(currentValue) > cardPrice;
                              
                              return (
                              <div key={trainingType}>
                                <Label className="text-xs text-amber-600">{trainingType}</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max={cardConfig.type === "percentage" ? 100 : maxFixedValue}
                                  step="1"
                                  value={currentValue}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    // Only allow valid numbers
                                    if (inputValue === "" || /^\d*\.?\d*$/.test(inputValue)) {
                                      let value = inputValue === "" ? "" : parseFloat(inputValue) || 0;
                                      // Yüzde için max 100
                                      if (cardConfig.type === "percentage" && value > 100) {
                                        value = 100;
                                      }
                                      // Sabit için max kart fiyatı
                                      if (cardConfig.type === "fixed" && cardPrice > 0 && value > cardPrice) {
                                        value = cardPrice;
                                        toast.error(t("reports:commissionExceedsCardPrice"));
                                      }
                                      setNewTrainerCommissionSettings(prev => ({
                                        ...prev,
                                        card_commissions: {
                                          ...prev.card_commissions,
                                          [cardName]: {
                                            ...cardConfig,
                                            rates: { ...cardConfig.rates, [trainingType]: value }
                                          }
                                        }
                                      }));
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    // Block non-numeric keys except navigation and decimal
                                    if (!/[\d.\-]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  className={`rounded-lg h-8 text-sm ${isOverMax ? 'border-red-500' : ''}`}
                                  placeholder={cardConfig.type === "fixed" ? (cardPrice > 0 ? `Max ${currencySymbol}${cardPrice}` : currencySymbol) : "% (0-100)"}
                                  disabled={cardConfig.type === "fixed" && cardPrice <= 0}
                                />
                                {isOverMax && (
                                  <p className="text-xs text-red-500 mt-0.5">Max: {currencySymbol}{cardPrice}</p>
                                )}
                              </div>
                            )})}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* {t("packages:packageCommissions")} */}
                <div className="p-3 bg-emerald-50 rounded-xl space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium text-emerald-800 text-sm flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                      {t("packages:packageCommissions")}
                    </Label>
                    <Select 
                      value={newTrainerCommissionSettings.package_commissions?.type || "percentage"} 
                      onValueChange={(v) => setNewTrainerCommissionSettings(prev => ({
                        ...prev,
                        package_commissions: { ...prev.package_commissions, type: v }
                      }))}
                    >
                      <SelectTrigger className="w-28 h-8 rounded-lg text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                        <SelectItem value="fixed">{t("common:fixed")} ({currencySymbol})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {businessTrainingTypes.map(trainingType => {
                      const packageConfig = newTrainerCommissionSettings.package_commissions || { type: "percentage", rates: {} };
                      return (
                        <div key={trainingType}>
                          <Label className="text-xs text-emerald-700">{trainingType}</Label>
                          <Input
                            type="number"
                            min="0"
                            max={packageConfig.type === "percentage" ? 100 : undefined}
                            step="1"
                            value={packageConfig.rates?.[trainingType] || ""}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              // Only allow valid numbers
                              if (inputValue === "" || /^\d*\.?\d*$/.test(inputValue)) {
                                let value = inputValue === "" ? "" : parseFloat(inputValue) || 0;
                                // Yüzde için max 100
                                if (packageConfig.type === "percentage" && value > 100) {
                                  value = 100;
                                }
                                setNewTrainerCommissionSettings(prev => ({
                                  ...prev,
                                  package_commissions: {
                                    ...prev.package_commissions,
                                    rates: { ...prev.package_commissions?.rates, [trainingType]: value }
                                  }
                                }));
                              }
                            }}
                            onKeyDown={(e) => {
                              // Block non-numeric keys except navigation and decimal
                              if (!/[\d.\-]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            className="rounded-lg h-8 text-sm"
                            placeholder={packageConfig.type === "fixed" ? currencySymbol : "%"}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              )}

              <Button
                className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleAddTrainer}
                loading={isAddingTrainer}
                loadingText="Ekleniyor..."
                data-testid="save-trainer-btn"
              >
                {t("common:addTrainer")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
          )}
        </div>
        
        {!canEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            {t("admin:subscriptionRequiredForTrainer")}
          </div>
        )}
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
            <Input
              placeholder={t('trainers:searchTrainer')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
              data-testid="search-trainers"
            />
          </div>
        </CardContent>
      </Card>

      {/* Trainers List/Grid */}
      {filteredTrainers.length > 0 ? (
        viewMode === 'list' ? (
          <>
            {/* Desktop Table View */}
            <Card className="rounded-2xl hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("name")}
                    >
                      <span className="flex items-center">
                        {t("common:trainerLabel2")} <SortIcon field="name" />
                      </span>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("work_type")}
                    >
                      <span className="flex items-center">
                        {t("common:workType")} <SortIcon field="work_type" />
                      </span>
                    </TableHead>
                    <TableHead>{t('business:rating')}</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("start_date")}
                    >
                      <span className="flex items-center">
                        {t("common:start")} <SortIcon field="start_date" />
                      </span>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort("status")}
                    >
                      <span className="flex items-center">
                        Durum <SortIcon field="status" />
                      </span>
                    </TableHead>
                    <TableHead>{t('employees:manager')}</TableHead>
                    <TableHead className="text-right">{t('common:actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTrainers.map((trainer) => (
                    <TableRow
                      key={trainer.trainer_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/dashboard/trainers/${trainer.trainer_id}`)}
                      data-testid={`trainer-row-${trainer.trainer_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-navy/10 text-navy text-xs font-bold">
                              {trainer.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-navy">{trainer.name} {trainer.surname}</p>
                            <div className="flex flex-col text-xs text-grey">
                              {trainer.phone && <span>{trainer.phone}</span>}
                              {trainer.email && <span>{trainer.email}</span>}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={`text-xs ${getCommissionTypeColor(trainer.work_type)}`}>
                          {getCommissionTypeLabel(trainer.work_type, t)}
                        </Badge>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {trainer.branches?.slice(0, 2).map((branch) => (
                            <span key={branch} className="text-xs text-grey">{branch}</span>
                          ))}
                          {trainer.branches?.length > 2 && (
                            <span className="text-xs text-grey">+{trainer.branches.length - 2}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span>{trainer.rating?.toFixed(1) || "5.0"}</span>
                          <span className="text-grey text-xs">({trainer.rating_count || 0})</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{trainer.start_date ? format(new Date(trainer.start_date), "dd/MM/yyyy", { locale: getDateLocale() }) : "-"}</span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={(trainer.status === "active" || trainer.is_active) ? "default" : "secondary"}
                          className={(trainer.status === "active" || trainer.is_active) ? "bg-green-100 text-green-700" : ""}
                        >
                          {(trainer.status === "active" || trainer.is_active) ? t('admin:active') : t("common:left")}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={trainer.is_manager || false}
                          onCheckedChange={(checked) => handleToggleManager(trainer.trainer_id, checked)}
                        />
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleEditTrainer(trainer, e)}
                            className="text-grey hover:text-navy"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleToggleStatus(trainer, e)}
                            className={(trainer.status === "active" || trainer.is_active)
                              ? "text-amber-500 hover:text-amber-700 hover:bg-amber-50" 
                              : "text-green-500 hover:text-green-700 hover:bg-green-50"}
                            title={(trainer.status === "active" || trainer.is_active) ? "Pasif Yap" : "Aktif Yap"}
                            data-testid={`toggle-status-${trainer.trainer_id}`}
                          >
                            {(trainer.status === "active" || trainer.is_active) ? (
                              <AlertCircle className="w-4 h-4" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteTrainer(trainer.trainer_id, e)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-trainer-${trainer.trainer_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-grey" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {sortedTrainers.map((trainer) => (
                <Card
                  key={trainer.trainer_id}
                  className="border-0 shadow-sm cursor-pointer active:bg-slate-50"
                  onClick={() => navigate(`/dashboard/trainers/${trainer.trainer_id}`)}
                  data-testid={`trainer-mobile-${trainer.trainer_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Avatar className="h-10 w-10 flex-shrink-0">
                          <AvatarFallback className="bg-navy text-white text-sm">
                            {trainer.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-navy truncate">
                              {trainer.name} {trainer.surname}
                            </p>
                            {trainer.is_manager && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs flex-shrink-0">
                                <ShieldCheck className="w-3 h-3" />
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-col text-xs text-grey">
                            {trainer.phone && <span>{trainer.phone}</span>}
                            {trainer.email && <span>{trainer.email}</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                              <span className="text-xs text-grey">{trainer.rating?.toFixed(1) || "5.0"}</span>
                            </div>
                            {trainer.experience_years > 0 && (
                              <span className="text-xs text-navy font-medium">{t("common:yearsExp", { years: trainer.experience_years })}</span>
                            )}
                            {trainer.branches?.slice(0, 2).map((branch) => (
                              <Badge key={branch} variant="secondary" className="text-xs py-0">
                                {getLocalizedBranch(branch)}
                              </Badge>
                            ))}
                            {trainer.branches?.length > 2 && (
                              <span className="text-xs text-grey">+{trainer.branches.length - 2}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Badge
                          variant={(trainer.status === "active" || trainer.is_active) ? "default" : "secondary"}
                          className={`text-xs ${(trainer.status === "active" || trainer.is_active) ? "bg-green-100 text-green-700" : ""}`}
                        >
                          {(trainer.status === "active" || trainer.is_active) ? t('admin:active') : t("common:left")}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-grey" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          /* Grid View - Rich card format */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTrainers.map((trainer) => (
              <Card
                key={trainer.trainer_id}
                className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/dashboard/trainers/${trainer.trainer_id}`)}
                data-testid={`trainer-card-${trainer.trainer_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-navy text-white">
                          {trainer.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-heading font-bold text-lg text-navy">
                          {trainer.name} {trainer.surname}
                        </h3>
                        <div className="flex flex-col text-xs text-grey mb-1">
                          {trainer.phone && <span>{trainer.phone}</span>}
                          {trainer.email && <span>{trainer.email}</span>}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-grey">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span>{trainer.rating?.toFixed(1) || "5.0"}</span>
                          <span className="text-xs">({trainer.rating_count || 0})</span>
                          {trainer.experience_years > 0 && (
                            <span className="text-navy font-medium ml-2">• {t("common:yearsExp", { years: trainer.experience_years })}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {trainer.is_manager && (
                        <Badge className="bg-purple-100 text-purple-700 text-xs">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          {t("admin:admin")}
                        </Badge>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={(e) => handleDeleteTrainer(trainer.trainer_id, e)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {/* Çalışma Şekli */}
                  <div className="mb-3">
                    <p className="text-xs text-grey mb-1.5">{t("common:workingType")}</p>
                    <Badge variant="secondary" className={`text-xs ${getCommissionTypeColor(trainer.work_type)}`}>
                      {getCommissionTypeLabel(trainer.work_type, t)}
                    </Badge>
                  </div>

                  {/* Branşlar */}
                  {trainer.branches?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-grey mb-1.5">{t('common:branches')}</p>
                      <div className="flex flex-wrap gap-1">
                        {trainer.branches.slice(0, 3).map((branch) => (
                          <Badge key={branch} variant="secondary" className="text-xs">
                            {getLocalizedBranch(branch)}
                          </Badge>
                        ))}
                        {trainer.branches.length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{trainer.branches.length - 3}</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Durum */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-xs text-grey">
                      {trainer.start_date ? format(new Date(trainer.start_date), "dd/MM/yyyy", { locale: getDateLocale() }) : "-"}
                    </span>
                    <Badge
                      variant={(trainer.status === "active" || trainer.is_active) ? "default" : "secondary"}
                      className={(trainer.status === "active" || trainer.is_active) ? "bg-green-100 text-green-700" : ""}
                    >
                      {(trainer.status === "active" || trainer.is_active) ? t('admin:active') : t("common:left")}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : (
        <Card className="rounded-2xl">
          <CardContent className="p-8 text-center">
            <Dumbbell className="w-12 h-12 mx-auto text-grey/30 mb-4" />
            <h3 className="text-lg font-semibold text-grey">{searchQuery ? t("common:noTrainerFound2") : t("common:noTrainerAdded")}</h3>
            <p className="text-sm text-grey/70 mt-1">{t("common:i̇lkAntrenorunuzuEkleyin")}</p>
            <Button onClick={() => setShowAddModal(true)} className="mt-4 bg-navy hover:bg-navy/90 rounded-xl" disabled={!canEdit}>
              <Plus className="w-4 h-4 mr-2" /> {t("common:newTrainer")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Trainer Detail Modal - With Tabs like EmployeesModule */}
      {selectedTrainer && (
        <Dialog open={!!selectedTrainer} onOpenChange={() => setSelectedTrainer(null)}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-navy text-white font-bold">
                    {(selectedTrainer?.name?.[0] || "A").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-bold text-navy">{selectedTrainer?.name} {selectedTrainer?.surname}</h3>
                  <p className="text-sm text-grey font-normal flex items-center gap-1">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    {selectedTrainer?.rating?.toFixed(1) || "5.0"} ({selectedTrainer?.rating_count || 0})
                  </p>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Quick Action Buttons - Like EmployeesModule */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button variant="outline" size="sm" className="rounded-xl flex flex-col items-center gap-1 h-auto py-2" onClick={() => { setScheduleTrainer(selectedTrainer); setShowScheduleModal(true); }}>
                  <Clock className="w-4 h-4" />
                  <span className="text-xs">{t('settings:workingHours')}</span>
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex flex-col items-center gap-1 h-auto py-2" onClick={() => { setScheduleTrainer(selectedTrainer); setShowTimeBlockModal(true); }}>
                  <Lock className="w-4 h-4" />
                  <span className="text-xs">{t("common:timeLock")}</span>
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex flex-col items-center gap-1 h-auto py-2" onClick={() => { setScheduleTrainer(selectedTrainer); setShowCommissionReportModal(true); }}>
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs">{t("reports:commissionReportLabel")}</span>
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex flex-col items-center gap-1 h-auto py-2" onClick={() => { setSelectedTrainer(null); openBonusModal(selectedTrainer); }}>
                  <Gift className="w-4 h-4" />
                  <span className="text-xs">{t("common:extraPayment")}</span>
                </Button>
              </div>

              {/* Çalışma Şekli */}
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-grey mb-1">{t("common:workingType")}</p>
                <p className="text-sm font-medium flex items-center gap-2">
                  {(() => {
                    const wt = selectedTrainer.work_type;
                    if (wt === "commission_receiving") return <><TrendingUp className="w-3.5 h-3.5 text-green-600" /> {t("reports:commissionReceiving")}</>;
                    if (wt === "salary_receiving") return <><Wallet className="w-3.5 h-3.5 text-blue-600" /> {t("employees:salaryReceiving")}</>;
                    if (wt === "salary_and_commission_receiving") return <><Wallet className="w-3.5 h-3.5 text-purple-600" /> {t("reports:salaryAndCommission")}</>;
                    if (wt === "commission_giving") return <><ArrowUpRight className="w-3.5 h-3.5 text-orange-600" /> {t("reports:commissionGiving")}</>;
                    if (wt === "rent_giving") return <><Building2 className="w-3.5 h-3.5 text-amber-600" /> {t("common:rentGiving")}</>;
                    return <><TrendingUp className="w-3.5 h-3.5 text-green-600" /> {t("reports:commissionReceiving")}</>;
                  })()}
                </p>
              </div>

              {/* İşletmeye Verilen Prim/Kira - commission_giving ve rent_giving için */}
              {selectedTrainer.work_type === 'commission_giving' && (
                <div className="p-3 bg-orange-50 rounded-xl border border-orange-200">
                  <p className="text-xs text-orange-700 mb-1 font-medium">{t("reports:commGivenToBusiness")}</p>
                  <div className="flex items-center gap-2">
                    <ArrowUpRight className="w-4 h-4 text-orange-600" />
                    <span className="text-lg font-bold text-orange-700">
                      {selectedTrainer.giving_commission_type === "fixed" ? currencySymbol : "%"}{selectedTrainer.giving_commission_rate || 0}
                    </span>
                    <span className="text-xs text-orange-600">her dersten</span>
                  </div>
                </div>
              )}

              {selectedTrainer.work_type === 'rent_giving' && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-xs text-amber-700 mb-1 font-medium">{t("common:aylikKira")}</p>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-amber-600" />
                    <span className="text-lg font-bold text-amber-700">{currencySymbol}{(selectedTrainer.rent_amount || 0).toLocaleString('tr-TR')}</span>
                    <span className="text-xs text-amber-600">/ay</span>
                  </div>
                </div>
              )}

              {/* Maaş - if applicable */}
              {(selectedTrainer.work_type === "salary_receiving" || selectedTrainer.work_type === "salary_and_commission_receiving") && selectedTrainer.current_salary > 0 && (
                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-xs text-green-700 mb-1 font-medium">{t('trainers:salary')}</p>
                  <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-green-600" />
                    <span className="text-lg font-bold text-green-700">{currencySymbol}{(selectedTrainer.current_salary || 0).toLocaleString('tr-TR')}</span>
                    <span className="text-xs text-green-600">/ ay</span>
                  </div>
                </div>
              )}

              {/* Prim bilgileri - Prim alan TÜM tipler için */}
              {['commission_receiving', 'salary_and_commission_receiving', 'commission_giving', 'rent_giving'].includes(selectedTrainer.work_type) && (
                <>
                  {/* Prim Tipi */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-xs text-grey mb-1">Prim Tipi</p>
                      <p className="text-sm font-medium flex items-center gap-2">
                        {(() => {
                          const cs = selectedTrainer.commission_settings;
                          if (cs) {
                            const types = new Set();
                            if (cs.card_commissions) {
                              Object.values(cs.card_commissions).forEach(card => {
                                if (card.type) types.add(card.type);
                              });
                            }
                            if (cs.package_commissions?.type) {
                              types.add(cs.package_commissions.type);
                            }
                            
                            if (types.has("percentage") && types.has("fixed")) {
                              return <><Percent className="w-3.5 h-3.5 text-navy" /><Banknote className="w-3.5 h-3.5 text-navy" /> {t("common:percentageAndFixed2")}</>;
                            } else if (types.has("percentage")) {
                              return <><Percent className="w-3.5 h-3.5 text-navy" /> {t("common:percentageBased")}</>;
                            } else if (types.has("fixed")) {
                              return <><Banknote className="w-3.5 h-3.5 text-navy" /> {t("common:fixed")} ({currencySymbol})</>;
                            }
                          }
                          return selectedTrainer.commission_type === "percentage" 
                            ? <><Percent className="w-3.5 h-3.5 text-navy" /> {t("common:percentageBased")}</>
                            : <><Banknote className="w-3.5 h-3.5 text-navy" /> {t("common:perLessonLabel")}</>;
                        })()}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-xs text-grey mb-1">{t('settings:startTime')}</p>
                      <p className="text-sm font-medium flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-navy" />
                        {selectedTrainer.start_date ? format(new Date(selectedTrainer.start_date), "d MMM yyyy", { locale: getDateLocale() }) : "-"}
                      </p>
                    </div>
                  </div>

                  {/* Prim Hesaplama Yöntemi */}
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <p className="text-xs text-grey mb-1">{t('trainers:commissionCalculation')}</p>
                    <p className="text-sm font-medium text-navy">
                      {selectedTrainer.commission_settings?.calculation_method === "per_package_sale" ? `📦 ${t("packages:perPackageSaleMethod")}` : `📚 ${t("reports:perLessonCommMethod")}`}
                    </p>
                    {selectedTrainer.commission_settings?.calculation_method === "per_package_sale" && (
                      <p className="text-xs text-blue-600 mt-1">
                        {selectedTrainer.commission_settings?.commission_scope === "all_lessons"
                          ? t("packages:allPackageSalesCommShort")
                          : t("reports:ownLessonsCommShort")}
                      </p>
                    )}
                  </div>

                  {/* Bu Ay Prim Özeti */}
                  <div className="p-3 bg-purple-50 rounded-xl">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs font-medium text-purple-700">{t("reports:thisMonthCommissionSummary")}</p>
                      <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                        {new Date().toLocaleString('tr-TR', { month: 'long', year: 'numeric' })}
                      </Badge>
                    </div>
                    {trainerCommission ? (
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center p-1.5 bg-white rounded-lg">
                          <p className="text-xs text-grey">Ders</p>
                          <p className="text-base font-bold text-navy">{trainerCommission.total_sessions || 0}</p>
                        </div>
                        <div className="text-center p-1.5 bg-white rounded-lg">
                          <p className="text-xs text-grey">{t('common:total')}</p>
                          <p className="text-base font-bold text-purple-700">{currencySymbol}{(trainerCommission.total_commission || 0).toLocaleString('tr-TR')}</p>
                        </div>
                        <div className="text-center p-1.5 bg-white rounded-lg">
                          <p className="text-xs text-grey">Bekleyen</p>
                          <p className="text-base font-bold text-orange-600">{currencySymbol}{(trainerCommission.pending_amount || 0).toLocaleString('tr-TR')}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-grey text-center py-1">{t('common:loading')}</p>
                    )}
                  </div>

                  {/* Branşlar */}
                  <div>
                    <p className="text-xs text-grey mb-1">{t('common:branches')}</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedTrainer.branches?.map((branch) => (
                        <Badge key={branch} variant="secondary" className="text-xs">{branch}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Prim Oranları */}
                  {selectedTrainer.commission_settings && (
                    <div>
                      <p className="text-xs text-grey mb-1">{t("reports:commissionRates")}</p>
                      <div className="space-y-2">
                        {/* Card Commissions */}
                        {businessAcceptedCards.length > 0 && selectedTrainer.commission_settings.card_commissions && (
                          <div>
                            <p className="text-xs font-medium text-grey-600 mb-1 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                              {t("reports:cardCommissions")}
                            </p>
                            <div className="space-y-1">
                              {businessAcceptedCards.map(cardName => {
                                const cardConfig = selectedTrainer.commission_settings?.card_commissions?.[cardName];
                                if (!cardConfig) return null;
                                return (
                                  <div key={cardName} className="p-1.5 bg-amber-50 rounded-lg">
                                    <p className="text-xs font-medium text-amber-700 mb-0.5">{cardName} ({cardConfig.type === "percentage" ? "%" : currencySymbol})</p>
                                    <div className="flex flex-wrap gap-1">
                                      {businessTrainingTypes.map(type => (
                                        <span key={type} className="text-xs bg-amber-100 px-1.5 py-0.5 rounded">
                                          {type}: {cardConfig.type === "percentage" ? "%" : currencySymbol}{cardConfig.rates?.[type] || 0}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Package Commissions */}
                        {selectedTrainer.commission_settings.package_commissions && (
                          <div>
                            <p className="text-xs font-medium text-grey-600 mb-1 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                              {t("packages:packageCommissions")} ({selectedTrainer.commission_settings.package_commissions.type === "percentage" ? "%" : currencySymbol})
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {businessTrainingTypes.map(type => (
                                <span key={type} className="text-xs bg-emerald-100 px-1.5 py-0.5 rounded text-emerald-700">
                                  {type}: {selectedTrainer.commission_settings.package_commissions.type === "percentage" ? "%" : currencySymbol}{selectedTrainer.commission_settings.package_commissions.rates?.[type] || 0}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Bottom Actions */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { openEditModal(selectedTrainer); setSelectedTrainer(null); }}>
                  <Edit className="w-4 h-4 mr-2" /> {t("common:edit")}
                </Button>
                {(selectedTrainer.status === "active" || selectedTrainer.is_active) && (
                  <Button variant="outline" className="rounded-xl text-red-600 hover:bg-red-50" onClick={() => setShowEndModal(true)}>
                    <UserX className="w-4 h-4 mr-2" /> {t("common:left")}
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Trainer Schedule Modal */}
      <TrainerScheduleModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setScheduleTrainer(null);
        }}
        trainer={scheduleTrainer}
        businessId={businessId}
        businessWorkingHours={businessWorkingHours}
      />

      {/* Trainer Time Block Modal */}
      <TrainerTimeBlockModal
        isOpen={showTimeBlockModal}
        onClose={() => {
          setShowTimeBlockModal(false);
          setScheduleTrainer(null);
        }}
        trainer={scheduleTrainer}
        businessId={businessId}
        canEdit={true}
      />

      {/* Trainer Commission Report Modal */}
      <TrainerCommissionReportModal
        isOpen={showCommissionReportModal}
        onClose={() => {
          setShowCommissionReportModal(false);
          setScheduleTrainer(null);
        }}
        trainer={scheduleTrainer}
        businessId={businessId}
      />

      {/* End Trainer Modal */}
      <Dialog open={showEndModal} onOpenChange={setShowEndModal}>
        <DialogContent className="max-w-md w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle>{t("common:trainerDeparture")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-grey">
              <strong>{selectedTrainer?.name} {selectedTrainer?.surname}</strong> {t("common:specifyLeaveReason")}
            </p>
            <div>
              <Label>{t("common:leavingReason")}</Label>
              <Select value={endReason} onValueChange={(val) => {
                setEndReason(val);
                if (val !== "diger") setEndOtherReason("");
              }}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_nedenSecin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="istifa">{t("common:resignation")}</SelectItem>
                  <SelectItem value="performans">{t("common:poorPerformance")}</SelectItem>
                  <SelectItem value="disiplin">Disiplin</SelectItem>
                  <SelectItem value="karsilikli">{t("common:mutualAgreement")}</SelectItem>
                  <SelectItem value="diger">{t('common:other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Diğer seçildiğinde açıklama alanı */}
            {endReason === "diger" && (
              <div>
                <Label>{t("admin:departureReasonExplanation")}</Label>
                <Input
                  value={endOtherReason}
                  onChange={(e) => setEndOtherReason(e.target.value)}
                  placeholder={t("common:ph_ayrilmaNedeniniAciklayin")}
                  className="rounded-xl mt-1"
                />
              </div>
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowEndModal(false)}
              >
                {t("common:cancel")}
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 rounded-xl"
                onClick={handleEndTrainer}
              >
                Onayla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Trainer Modal - Enhanced Commission System */}
      {/* Trainer Edit Modal - With Tabs like EmployeesModule */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('trainers:editTrainer')}</DialogTitle>
          </DialogHeader>
          
          {editingTrainer && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">{t("common:info")}</TabsTrigger>
                <TabsTrigger value="commission">{t("reports:commissionsTab")}</TabsTrigger>
                <TabsTrigger value="permissions">{t("employees:permissionsTab")}</TabsTrigger>
              </TabsList>

              {/* Bilgiler Tab */}
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('common:firstName')}</Label>
                    <Input value={editingTrainer.name || ""} onChange={(e) => setEditingTrainer({...editingTrainer, name: e.target.value})} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label>{t("common:surnameLabel")}</Label>
                    <Input value={editingTrainer.surname || ""} onChange={(e) => setEditingTrainer({...editingTrainer, surname: e.target.value})} className="rounded-xl mt-1" />
                  </div>
                </div>

                <div>
                  <Label>{t('common:branches')}</Label>
                  <p className="text-xs text-grey mb-2">{t("employees:i̇sletmenizinBranslariIleAntrenorunBildi")}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(() => {
                      const trainerKnownBranches = editingTrainer.known_branches || [];
                      const availableBranches = branches.filter(b => 
                        trainerKnownBranches.length === 0 || trainerKnownBranches.includes(b.name)
                      );
                      
                      if (trainerKnownBranches.length > 0 && availableBranches.length === 0) {
                        return (
                          <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                            <p className="font-medium">⚠️ {t("common:branchMismatch")}</p>
                            <p className="mt-1">{t("common:business2")} {branches.length > 0 ? branches.map(b => b.name).join(", ") : t("common:none2")}</p>
                            <p>{t("common:trainerLabel2")}: {trainerKnownBranches.join(", ")}</p>
                          </div>
                        );
                      }
                      
                      return availableBranches.map((branch) => (
                        <button
                          key={branch.id || branch.name}
                          type="button"
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            editingTrainer.branches?.includes(branch.name)
                              ? "bg-navy text-white"
                              : "bg-grey-100 text-grey-700 hover:bg-grey-200"
                          }`}
                          onClick={() => {
                            const newBranches = editingTrainer.branches?.includes(branch.name)
                              ? editingTrainer.branches.filter(b => b !== branch.name)
                              : [...(editingTrainer.branches || []), branch.name];
                            setEditingTrainer({...editingTrainer, branches: newBranches});
                          }}
                        >
                          {getLocalizedBranch(branch.name)}
                        </button>
                      ));
                    })()}
                  </div>
                </div>

                {/* İşe Başlama Tarihi */}
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <h4 className="font-semibold text-green-700 flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4" /> {t("common:startDate2")}
                  </h4>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-grey">{t("common:current")}</span>
                    <span className="font-medium text-green-700">
                      {editingTrainer.start_date 
                        ? format(new Date(editingTrainer.start_date), "dd/MM/yyyy", { locale: getDateLocale() })
                        : t("common:notSpecified")}
                    </span>
                  </div>
                  <div>
                    <Label className="text-sm">{t("common:enterNewDate")}</Label>
                    <DatePickerTR
                      value={editingTrainer.start_date ? new Date(editingTrainer.start_date) : null}
                      onChange={(date) => setEditingTrainer({...editingTrainer, start_date: date ? formatDateToLocal(date) : ""})}
                      placeholder={t('common:selectDate')}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>{t("common:workingType")}</Label>
                  <Select
                    value={editingTrainer.work_type || "commission_receiving"}
                    onValueChange={(value) => {
                      const oldType = editingTrainer.work_type;
                      const updates = { work_type: value };
                      
                      // Clear ALL fields from the OLD work type
                      // Prim Alan fields
                      if (oldType === 'commission_receiving' || oldType === 'salary_and_commission_receiving') {
                        if (value !== 'commission_receiving' && value !== 'salary_and_commission_receiving') {
                          updates.commission_settings = {};
                          updates.commission_type = null;
                          updates.commission_rates = {};
                        }
                      }
                      
                      // Maaş Alan fields
                      if (oldType === 'salary_receiving' || oldType === 'salary_and_commission_receiving') {
                        if (value !== 'salary_receiving' && value !== 'salary_and_commission_receiving') {
                          updates.current_salary = 0;
                          updates.salary_start_date = null;
                        }
                      }
                      
                      // Prim Veren fields
                      if (oldType === 'commission_giving') {
                        updates.giving_commission_type = null;
                        updates.giving_commission_rate = 0;
                      }
                      
                      // Kira Veren fields
                      if (oldType === 'rent_giving') {
                        updates.rent_amount = 0;
                        updates.rent_start_date = null;
                      }
                      
                      // Clear own_business_id when switching from giving types to receiving types
                      if ((oldType === 'commission_giving' || oldType === 'rent_giving') &&
                          (value === 'commission_receiving' || value === 'salary_receiving' || value === 'salary_and_commission_receiving')) {
                        updates.own_business_id = null;
                        updates.own_business_name = null;
                      }
                      
                      setEditingTrainer({...editingTrainer, ...updates});
                    }}
                  >
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commission_receiving">{t("reports:commissionReceiving")}</SelectItem>
                      <SelectItem value="salary_receiving">💵 {t("employees:salaryReceiving")}</SelectItem>
                      <SelectItem value="salary_and_commission_receiving">💎 {t("reports:salaryAndCommission")}</SelectItem>
                      <div className="my-1 border-t border-dashed border-grey-300">
                        <p className="text-xs text-grey px-2 py-1 bg-grey-50">{t("common:forTrainersWithOwnBusiness")}</p>
                      </div>
                      <SelectItem value="commission_giving">📤 {t("reports:commissionGiving")}</SelectItem>
                      <SelectItem value="rent_giving">🏠 {t("common:rentGiving")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Maaş Ayarları - salary_receiving veya salary_and_commission_receiving için */}
                {(editingTrainer.work_type === 'salary_receiving' || editingTrainer.work_type === 'salary_and_commission_receiving') && (
                  <div className="p-4 bg-green-50 rounded-xl space-y-3 border border-green-200">
                    <h4 className="font-semibold text-green-700 flex items-center gap-2">
                      <Wallet className="w-4 h-4" /> {t("employees:salarySettings")}
                    </h4>
                    <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <span className="text-sm text-grey">{t("employees:currentSalary")}</span>
                      <span className="font-bold text-green-600">{currencySymbol}{(editingTrainer.current_salary || 0).toLocaleString('tr-TR')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">{t("employees:newSalary")}</Label>
                        <Input type="number" value={newSalaryAmount} onChange={(e) => setNewSalaryAmount(e.target.value)} placeholder={t("employees:ph_yeniMaas")} className="rounded-xl mt-1" />
                      </div>
                      <div>
                        <Label className="text-sm">{t("common:validityDate")}</Label>
                        <DatePickerTR value={salaryEffectiveDate ? new Date(salaryEffectiveDate) : null} onChange={(date) => setSalaryEffectiveDate(date ? formatDateToLocal(date) : "")} placeholder={t('common:selectDate')} className="mt-1" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Kendi İşletmesi - Düzenleme modunda Prim/Kira Veren için */}
                {(editingTrainer.work_type === 'commission_giving' || editingTrainer.work_type === 'rent_giving') && (
                  <div className="p-4 bg-orange-50 rounded-xl space-y-3 border border-orange-200">
                    <h4 className="font-semibold text-orange-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> {t("common:ownBusiness")}
                    </h4>
                    {editingTrainer.own_business_id ? (
                      <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <span className="text-sm text-orange-700">{t("common:currentBusiness")}</span>
                        <span className="font-bold text-orange-600">{editingTrainer.own_business_name || editingTrainer.own_business_id}</span>
                      </div>
                    ) : (
                      <p className="text-sm text-red-500">⚠️ {t("common:businessNotSelectedCannotAcceptExternal")}</p>
                    )}
                    <Select 
                      value={editingTrainer.own_business_id || ""} 
                      onValueChange={(v) => {
                        const selectedBiz = editingTrainerBusinesses.find(b => b.business_id === v);
                        setEditingTrainer({
                          ...editingTrainer, 
                          own_business_id: v,
                          own_business_name: selectedBiz?.name || ""
                        });
                      }}
                    >
                      <SelectTrigger className="rounded-xl border-orange-300">
                        <SelectValue placeholder={t("common:ph_i̇sletmeSecinVeyaDegistirin")} />
                      </SelectTrigger>
                      <SelectContent>
                        {editingTrainerBusinesses.length > 0 ? (
                          editingTrainerBusinesses.map(b => (
                            <SelectItem key={b.business_id} value={b.business_id}>
                              {b.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-business" disabled>{t("common:buAntrenorunKayitliIsletmesiYok")}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Prim Verme Ayarları - commission_giving için */}
                {editingTrainer.work_type === 'commission_giving' && (
                  <div className="p-4 bg-orange-50 rounded-xl space-y-3 border border-orange-200">
                    <h4 className="font-semibold text-orange-700 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> {t("common:shareToGiveBusiness")}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Tip</Label>
                        <Select value={editingTrainer.giving_commission_type || "percentage"} onValueChange={(v) => setEditingTrainer({...editingTrainer, giving_commission_type: v})}>
                          <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                            <SelectItem value="fixed">{t("common:fixed")} ({currencySymbol})</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">{editingTrainer.giving_commission_type === 'percentage' ? 'Oran (%)' : 'Tutar ({currencySymbol})'}</Label>
                        <Input 
                          type="number" 
                          min="0" 
                          max={editingTrainer.giving_commission_type === 'percentage' ? "100" : undefined} 
                          value={editingTrainer.giving_commission_rate || ""} 
                          onChange={(e) => setEditingTrainer({...editingTrainer, giving_commission_rate: e.target.value})} 
                          onBlur={(e) => {
                            // Ensure the final value is captured on blur
                            const finalValue = e.target.value;
                            if (finalValue !== editingTrainer.giving_commission_rate) {
                              setEditingTrainer({...editingTrainer, giving_commission_rate: finalValue});
                            }
                          }}
                          placeholder={editingTrainer.giving_commission_type === 'percentage' ? "20" : "100"} 
                          className="rounded-xl mt-1" 
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Kira Ayarları - rent_giving için */}
                {editingTrainer.work_type === 'rent_giving' && (
                  <div className="p-4 bg-amber-50 rounded-xl space-y-3 border border-amber-200">
                    <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                      <Home className="w-4 h-4" /> {t("common:rentSettings")}
                    </h4>
                    {editingTrainer.rent_amount > 0 && (
                      <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <span className="text-sm text-amber-700">Mevcut Kira:</span>
                        <span className="font-bold text-amber-600">{currencySymbol}{(editingTrainer.rent_amount || 0).toLocaleString('tr-TR')}/ay</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">{t("common:monthlyRent")}</Label>
                        <Input 
                          type="number" 
                          min="0" 
                          value={editingTrainer.rent_amount || ""} 
                          onChange={(e) => setEditingTrainer({...editingTrainer, rent_amount: e.target.value})} 
                          onBlur={(e) => {
                            const finalValue = e.target.value;
                            if (finalValue !== editingTrainer.rent_amount) {
                              setEditingTrainer({...editingTrainer, rent_amount: finalValue});
                            }
                          }}
                          placeholder="5000" 
                          className="rounded-xl mt-1" 
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('common:startDate')}</Label>
                        <DatePickerTR value={editingTrainer.rent_start_date ? new Date(editingTrainer.rent_start_date) : null} onChange={(date) => setEditingTrainer({...editingTrainer, rent_start_date: date ? formatDateToLocal(date) : ""})} placeholder={t('common:selectDate')} className="mt-1" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Yönetici Toggle */}
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">{t('employees:manager')}</p>
                      <p className="text-xs text-amber-600">{t("common:fullMenuAccess")}</p>
                    </div>
                  </div>
                  <Switch 
                    checked={editingTrainer.is_manager || false} 
                    onCheckedChange={(c) => {
                      setEditingTrainer(prev => {
                        const newPermissions = c 
                          ? prev.permissions 
                          : {
                              ...prev.permissions,
                              branches: false,
                              analytics: false,
                              subscription: false,
                              salary_payments: false,
                              bonus_payments: false,
                              business_expenses: false,
                              trainer_collections: false,
                              commissions: false,
                              reports: false,
                              rooms: false,
                              packages: false,
                              price_list: false,
                              trainers: false,
                              employees: false,
                              settings: false
                            };
                        return {
                          ...prev,
                          is_manager: c,
                          permissions: newPermissions
                        };
                      });
                    }} 
                  />
                </div>
              </TabsContent>

              {/* Prim Tab */}
              <TabsContent value="commission" className="space-y-4 mt-4">
                {editingTrainer.work_type === "salary_receiving" ? (
                  <div className="text-center py-8 text-grey">
                    <Banknote className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>{t("reports:employeeSalaryOnlyNoCommission")}</p>
                  </div>
                ) : (
                  <>
                    {['commission_giving', 'rent_giving'].includes(editingTrainer.work_type) && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                        ⚠️ {t("reports:commissionSettingsNote")}
                      </p>
                    )}
                    
                    <div className="p-3 bg-blue-50 rounded-xl space-y-2">
                      <Label className="font-medium text-blue-800">{t('trainers:commissionCalculation')}</Label>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border text-sm">
                          <input type="radio" checked={editingTrainer.commission_settings?.calculation_method !== "per_package_sale"} onChange={() => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, calculation_method: "per_lesson"}})} className="mt-0.5" />
                          <div><p className="font-medium">{t("common:perLessonLabel")}</p></div>
                        </label>
                        <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border text-sm">
                          <input type="radio" checked={editingTrainer.commission_settings?.calculation_method === "per_package_sale"} onChange={() => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, calculation_method: "per_package_sale"}})} className="mt-0.5" />
                          <div><p className="font-medium">📦 {t("packages:perPackageSale")}</p></div>
                        </label>
                        {editingTrainer.commission_settings?.calculation_method === "per_package_sale" && (
                          <div className="ml-6 p-3 bg-blue-100 rounded-lg space-y-2">
                            <Label className="text-xs font-medium text-blue-800">{t("reports:commissionScope")}</Label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                              <input
                                type="radio"
                                name="editCommissionScope"
                                checked={editingTrainer.commission_settings?.commission_scope !== "all_lessons"}
                                onChange={() => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, commission_scope: "own_lessons"}})}
                              />
                              <span>Kendi derslerinden</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                              <input
                                type="radio"
                                name="editCommissionScope"
                                checked={editingTrainer.commission_settings?.commission_scope === "all_lessons"}
                                onChange={() => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, commission_scope: "all_lessons"}})}
                              />
                              <span>{t("common:fromAllLessons")}</span>
                            </label>
                            <p className="text-xs text-blue-700">
                              {editingTrainer.commission_settings?.commission_scope === "all_lessons"
                                ? t("packages:allPackageSalesComm")
                                : t("packages:ownPackageSalesComm")}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card Commissions */}
                    {businessAcceptedCards.length > 0 && (
                      <div className="space-y-2">
                        <Label className="font-medium text-amber-800 text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                          {t("reports:cardCommissions")}
                        </Label>
                        {businessAcceptedCards.map(cardName => {
                          const cardConfig = editingTrainer.commission_settings?.card_commissions?.[cardName] || { type: "percentage", rates: {} };
                          const cardPrice = businessCardPrices[cardName] || 0;
                          return (
                            <div key={cardName} className="p-3 bg-amber-50 rounded-xl space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-amber-700 text-sm">{cardName}</span>
                                <Select value={cardConfig.type || "percentage"} onValueChange={(v) => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, card_commissions: {...editingTrainer.commission_settings?.card_commissions, [cardName]: { ...cardConfig, type: v }}}})}>
                                  <SelectTrigger className="w-28 h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                                    <SelectItem value="fixed">{t("common:fixed")} ({currencySymbol})</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {/* Kart fiyatı uyarısı - sabit prim için */}
                              {cardConfig.type === "fixed" && cardPrice <= 0 && (
                                <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                                  ⚠️ {t("common:cardPriceNotSet")}
                                </p>
                              )}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {businessTrainingTypes.map(trainingType => {
                                  const maxFixedValue = cardConfig.type === "fixed" ? cardPrice : undefined;
                                  const currentValue = cardConfig.rates?.[trainingType] || "";
                                  const isOverMax = cardConfig.type === "fixed" && cardPrice > 0 && parseFloat(currentValue) > cardPrice;
                                  
                                  return (
                                  <div key={trainingType}>
                                    <Label className="text-xs text-amber-600">{trainingType}</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      max={cardConfig.type === "percentage" ? 100 : maxFixedValue}
                                      step="1"
                                      value={currentValue}
                                      onChange={(e) => {
                                        let value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                                        // Yüzde için max 100
                                        if (cardConfig.type === "percentage" && value > 100) {
                                          value = 100;
                                        }
                                        // Sabit için max kart fiyatı
                                        if (cardConfig.type === "fixed" && cardPrice > 0 && value > cardPrice) {
                                          value = cardPrice;
                                          toast.error(t("reports:commissionExceedsCardPrice"));
                                        }
                                        setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, card_commissions: {...editingTrainer.commission_settings?.card_commissions, [cardName]: {...cardConfig, rates: {...cardConfig.rates, [trainingType]: value}}}}});
                                      }}
                                      className={`rounded-lg h-8 text-sm ${isOverMax ? 'border-red-500' : ''}`}
                                      placeholder={cardConfig.type === "fixed" ? (cardPrice > 0 ? `Max ${currencySymbol}${cardPrice}` : currencySymbol) : "% (0-100)"}
                                      disabled={cardConfig.type === "fixed" && cardPrice <= 0}
                                    />
                                    {isOverMax && (
                                      <p className="text-xs text-red-500 mt-0.5">Max: {currencySymbol}{cardPrice}</p>
                                    )}
                                  </div>
                                )})}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Package Commissions */}
                    <div className="space-y-2">
                      <Label className="font-medium text-emerald-800 text-sm flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                        {t("packages:packageCommissions")}
                      </Label>
                      <div className="p-3 bg-emerald-50 rounded-xl space-y-2">
                        <Select value={editingTrainer.commission_settings?.package_commissions?.type || "percentage"} onValueChange={(v) => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, package_commissions: {...editingTrainer.commission_settings?.package_commissions, type: v}}})}>
                          <SelectTrigger className="w-full h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                            <SelectItem value="fixed">{t("common:fixed")} ({currencySymbol})</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {businessTrainingTypes.map(type => (
                            <div key={type}>
                              <Label className="text-xs text-emerald-600">{type}</Label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={editingTrainer.commission_settings?.package_commissions?.rates?.[type] || ""}
                                onChange={(e) => {
                                  const value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                                  setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, package_commissions: {...editingTrainer.commission_settings?.package_commissions, rates: {...editingTrainer.commission_settings?.package_commissions?.rates, [type]: value}}}});
                                }}
                                className="rounded-lg h-8 text-sm"
                                placeholder={editingTrainer.commission_settings?.package_commissions?.type === "fixed" ? currencySymbol : "%"}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>

{/* İzinler Tab */}
              <TabsContent value="permissions" className="space-y-4 mt-4">
                {/* Şirket Ortağı (Kurucu Ortak) Şalteri */}
                <div className={`p-4 rounded-xl border-2 mb-3 ${editingTrainer?.is_co_owner ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editingTrainer?.is_co_owner ? 'bg-emerald-600' : 'bg-slate-300'}`}>
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-semibold text-navy flex items-center gap-2">
                          Şirket Ortağı (Tam Yetki)
                        </span>
                        <p className="text-xs text-grey">
                          {editingTrainer?.is_co_owner 
                            ? "Ana işletme sahibi gibi tüm finans, kasa, prim, çalışan ve yönetim ekranlarına tam erişim sağlar." 
                            : "Şirket ortağı olarak atanmamış."}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={editingTrainer?.is_co_owner || false}
                      onCheckedChange={(c) => {
                        if (c) {
                          setPreviousPermissions({
                            permissions: { ...editingTrainer.permissions },
                            is_manager: editingTrainer.is_manager,
                            can_approve_reservations: editingTrainer.can_approve_reservations
                          });

                          const allPermissionsTrue = {
                            dashboard: true, dashboard_overview: true, branches: true, analytics: true, subscription: true,
                            members: true, members_list: true, all_packages: true, member_forms: true, qr_scan: true, measurements: true,
                            can_deactivate_members: true, reservations: true, manual_reservation_complete: true, manual_reservation_approve: true,
                            backdate_reservations: true, lesson_note_edit: true, finances: true, payments: true, salary_payments: true,
                            bonus_payments: true, business_expenses: true, trainer_collections: true, commissions: true, reports: true,
                            settings: true, rooms: true, packages: true, price_list: true, trainers: true, employees: true,
                            settings_business_info: true, settings_pricing: true, settings_accruals: true, settings_working_hours: true,
                            settings_branches: true, settings_training_types: true, settings_measurements: true, settings_expense_types: true,
                            settings_numbering: true, settings_currency: true, member_no_edit: true, package_no_edit: true,
                            communication: true, messages: true, ratings: true, surveys: true, events: true, evaluations: true, goals: true,
                            can_view_revenue: true, transfer_package_sale: true
                          };

                          setEditingTrainer(prev => ({
                            ...prev,
                            is_co_owner: true,
                            is_manager: true,
                            can_approve_reservations: true,
                            permissions: allPermissionsTrue
                          }));
                        } else {
                          if (previousPermissions) {
                            setEditingTrainer(prev => ({
                              ...prev,
                              is_co_owner: false,
                              is_manager: previousPermissions.is_manager,
                              can_approve_reservations: previousPermissions.can_approve_reservations,
                              permissions: previousPermissions.permissions
                            }));
                            setPreviousPermissions(null);
                          } else {
                            setEditingTrainer(prev => ({ ...prev, is_co_owner: false }));
                          }
                        }
                      }}
                    />
                  </div>
                </div>
                {/* Yönetici Durumu */}
                <div className={`p-4 rounded-xl border-2 ${editingTrainer.is_manager ? 'bg-green-50 border-green-300' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editingTrainer.is_manager ? 'bg-green-500' : 'bg-slate-300'}`}>
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-semibold text-navy">{t('employees:manager')}</span>
                        <p className="text-xs text-grey">
                          {editingTrainer.is_manager 
                            ? t("employees:hasManagerPermissions") 
                            : t("employees:notManagerLimited")}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={editingTrainer.is_manager || false}
                      onCheckedChange={(c) => {
                        if (!c) {
                          // Yönetici kapatılınca yönetici-only izinleri kapat
                          setEditingTrainer({
                            ...editingTrainer, 
                            is_manager: false,
                            permissions: {
                              ...editingTrainer.permissions,
                              branches: false,
                              analytics: false,
                              subscription: false,
                              salary_payments: false,
                              bonus_payments: false,
                              business_expenses: false,
                              trainer_collections: false,
                              commissions: false,
                              reports: false,
                              rooms: false,
                              packages: false,
                              price_list: false,
                              trainers: false,
                              employees: false,
                              settings: false,
                            }
                          });
                        } else {
                          setEditingTrainer({...editingTrainer, is_manager: true});
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Yönetici değilse uyarı */}
                {!editingTrainer.is_manager && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs text-amber-800 flex items-center gap-2">
                      <Lock className="w-3 h-3" />
                      <span><strong>{t("employees:managerRequiredForBadge")}</strong></span>
                    </p>
                  </div>
                )}

                {/* İzin Şablonları */}
                <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <Label className="text-sm font-medium text-navy mb-2 block">{t("employees:permissionTemplates")}</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs rounded-lg bg-white hover:bg-green-50 hover:border-green-300"
                      disabled={!editingTrainer.is_manager}
                      onClick={() => {
                        const allPerms = {
                          dashboard: true, dashboard_overview: true, branches: true, analytics: true, subscription: true,
                          members: true, members_list: true, qr_scan: true, measurements: true,
                          reservations: true,
                          finances: true, payments: true, salary_payments: true, bonus_payments: true,
                          business_expenses: true, trainer_collections: true, commissions: true, reports: true,
                          rooms: true, packages: true, price_list: true, trainers: true, employees: true,
                          settings: true,
                          can_view_revenue: true
                        };
                        setEditingTrainer({...editingTrainer, permissions: allPerms});
                      }}
                    >
                      👑 {t("employees:fullPermissions")} {!editingTrainer.is_manager && `(${t("employees:managerRequired")})`}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs rounded-lg bg-white hover:bg-blue-50 hover:border-blue-300"
                      onClick={() => {
                        const dersPerms = {
                          dashboard: true, dashboard_overview: true, branches: false, analytics: false, subscription: false,
                          members: true, members_list: true, qr_scan: true, measurements: true,
                          reservations: true,
                          finances: false, payments: false, salary_payments: false, bonus_payments: false,
                          business_expenses: false, trainer_collections: false, commissions: false, reports: false,
                          rooms: false, packages: false, price_list: false, trainers: false, employees: false,
                          settings: false,
                          can_view_revenue: false
                        };
                        setEditingTrainer({...editingTrainer, permissions: dersPerms});
                      }}
                    >
                      📚 {t("common:lessonsOnly")}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs rounded-lg bg-white hover:bg-red-50 hover:border-red-300"
                      onClick={() => {
                        const noPerms = {
                          dashboard: false, dashboard_overview: false, branches: false, analytics: false, subscription: false,
                          members: false, members_list: false, qr_scan: false, measurements: false,
                          reservations: false,
                          finances: false, payments: false, salary_payments: false, bonus_payments: false,
                          business_expenses: false, trainer_collections: false, commissions: false, reports: false,
                          rooms: false, packages: false, price_list: false, trainers: false, employees: false,
                          settings: false,
                          can_view_revenue: false
                        };
                        setEditingTrainer({...editingTrainer, permissions: noPerms});
                      }}
                    >
                      🚫 {t("common:disableAll")}
                    </Button>
                  </div>
                </div>
                
                {/* Genel */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <LayoutDashboard className="w-4 h-4 text-navy" />
                    <span className="font-medium text-navy">{t("common:general")}</span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                      <span className="text-green-700">{t("common:mapLabel")}</span>
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">{t("common:public")}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("common:overview")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.dashboard_overview !== false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, dashboard_overview: c}
                        })}
                      />
                    </div>
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("common:branchesLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.branches || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, branches: c}
                        })}
                      />
                    </div>
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("reports:analyticsLabel2")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.analytics || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, analytics: c}
                        })}
                      />
                    </div>
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("admin:subscriptionLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.subscription || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, subscription: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Üye Yönetimi */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-navy" />
                    <span className="font-medium text-navy">{t("common:memberManagement")}</span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('members:title')}</span>
                      <Switch
                        checked={editingTrainer.permissions?.members_list !== false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, members_list: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("surveys:memberForms")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.member_forms || false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, member_forms: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("qr:qrScanLabel")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.qr_scan || false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, qr_scan: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("evaluations:measurements")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.measurements || false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, measurements: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('members:allPackages')}</span>
                      <Switch
                        checked={editingTrainer.permissions?.all_packages || false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, all_packages: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Rezervasyonlar */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-navy" />
                      <span className="font-medium text-navy">{t('common:reservations')}</span>
                    </div>
                    <Switch
                      checked={editingTrainer.permissions?.reservations !== false}
                      onCheckedChange={(c) => setEditingTrainer({
                        ...editingTrainer, 
                        permissions: {...editingTrainer.permissions, reservations: c}
                      })}
                    />
                  </div>
                  {/* Rezervasyon Onayı Yapabilir - Üye onayını atlayabilir */}
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                    <span className="text-grey flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {t("admin:directApprovalAuth")}
                      <span className="text-xs text-green-600 bg-green-100 px-1 rounded">{t("admin:skipMemberApproval")}</span>
                    </span>
                    <Switch
                      checked={editingTrainer.can_approve_reservations || false}
                      onCheckedChange={(c) => setEditingTrainer({
                        ...editingTrainer, 
                        can_approve_reservations: c
                      })}
                    />
                  </div>
                </div>

                {/* Ödemeler - Ana toggle yok, Paket Ödemeleri herkes için açık, diğerleri yönetici-only */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-4 h-4 text-navy" />
                    <span className="font-medium text-navy">{t('common:payments')}</span>
                  </div>
                  <div className="space-y-1.5">
                    {/* Paket Ödemeleri - Herkes için */}
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("packages:packagePayments")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.payments || false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, payments: c}
                        })}
                      />
                    </div>
                    {/* Maaş Ödemeleri - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("employees:salaryPaymentsLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.salary_payments || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, salary_payments: c}
                        })}
                      />
                    </div>
                    {/* Ek Ödemeler - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("common:extraPaymentsLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.bonus_payments || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, bonus_payments: c}
                        })}
                      />
                    </div>
                    {/* İşletme Giderleri - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("reports:businessExpensesPermLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.business_expenses || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, business_expenses: c}
                        })}
                      />
                    </div>
                    {/* Gelen Kira/Prim - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("reports:incomingRentPrim")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.trainer_collections || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, trainer_collections: c}
                        })}
                      />
                    </div>
                    {/* Primler - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("reports:commissionsLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.commissions || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, commissions: c}
                        })}
                      />
                    </div>
                    {/* Raporlar - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("reports:reportsLabel2")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingTrainer.permissions?.reports || false}
                        disabled={!editingTrainer.is_manager}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, reports: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* İşletme Ayarları */}
                <div className={`border rounded-xl p-3 space-y-2 ${!editingTrainer.is_manager ? 'border-amber-200 bg-amber-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-navy flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      {t("common:businessSettings")}
                      <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                    </span>
                    <Switch
                      checked={editingTrainer.permissions?.settings || false}
                      disabled={!editingTrainer.is_manager}
                      onCheckedChange={(c) => setEditingTrainer({
                        ...editingTrainer, 
                        permissions: {
                          ...editingTrainer.permissions, 
                          settings: c,
                          rooms: c ? editingTrainer.permissions?.rooms : false,
                          packages: c ? editingTrainer.permissions?.packages : false,
                          price_list: c ? editingTrainer.permissions?.price_list : false,
                          trainers: c ? editingTrainer.permissions?.trainers : false,
                          employees: c ? editingTrainer.permissions?.employees : false
                        }
                      })}
                    />
                  </div>
                  <div className={`ml-6 space-y-1.5 ${!editingTrainer.is_manager || editingTrainer.permissions?.settings === false ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('common:rooms')}</span>
                      <Switch
                        checked={editingTrainer.permissions?.rooms || false}
                        disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, rooms: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("packages:packagesLabel")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.packages || false}
                        disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, packages: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("common:priceListsLabel")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.price_list || false}
                        disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, price_list: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('common:trainers')}</span>
                      <Switch
                        checked={editingTrainer.permissions?.trainers || false}
                        disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, trainers: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('employees:employees')}</span>
                      <Switch
                        checked={editingTrainer.permissions?.employees || false}
                        disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, employees: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Ayarlar Sayfası - alt yetkilendirme (Yönetici-only) */}
                <div className="border rounded-xl p-3 space-y-2 border-amber-200 bg-amber-50/30" data-testid="settings-page-permissions-block-trainer">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-navy flex items-center gap-2">
                      <Layers className="w-4 h-4" /> {t("employees:settingsPagePermissions", "Ayarlar Sayfası")}
                      <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t("employees:manager")}</span>
                    </span>
                  </div>
                  <p className="text-xs text-grey">{t("employees:settingsPagePermissionsHint", "Sol menüdeki Ayarlar sayfasında hangi bölümleri görebileceğini seçin.")}</p>
                  <div className={`ml-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 ${!editingTrainer.is_manager ? "opacity-50" : ""}`}>
                    {[
                      { k: "settings_business_info", label: t("settings:businessInfo") },
                      { k: "settings_pricing", label: t("settings:cardPricesTitle") },
                      { k: "settings_accruals", label: t("settings:accrualDaysTitle") },
                      { k: "settings_working_hours", label: t("settings:workingHoursTitle") },
                      { k: "settings_branches", label: t("settings:branchesTitle") },
                      { k: "settings_training_types", label: t("settings:trainingTypesTitle") },
                      { k: "settings_measurements", label: t("settings:measurementParamsTitle") },
                      { k: "settings_expense_types", label: t("settings:expenseTypesTitle") },
                      { k: "settings_numbering", label: t("employees:numberingSettings", "Numaralandırma") },
                      { k: "settings_currency", label: t("common:currency") },
                      { k: "member_no_edit", label: t("employees:memberNoEdit", "Üye No Düzenleme") },
                      { k: "package_no_edit", label: t("employees:packageNoEdit", "Paket No Düzenleme") },
                    ].map((it) => (
                      <div key={it.k} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm" data-testid={`perm-${it.k}-trainer`}>
                        <span className="text-grey">{it.label}</span>
                        <Switch
                          checked={editingTrainer.permissions?.[it.k] || false}
                          disabled={!editingTrainer.is_manager}
                          onCheckedChange={(c) => setEditingTrainer({ ...editingTrainer, permissions: { ...editingTrainer.permissions, [it.k]: c } })}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* İletişim - Ana toggle ile açılıp kapanabilir */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-navy" />
                      <span className="font-medium text-navy">{t('business:contact')}</span>
                    </div>
                    <Switch
                      checked={editingTrainer.permissions?.communication !== false}
                      onCheckedChange={(c) => {
                        // Ana toggle - alt izinleri de etkiler
                        setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {
                            ...editingTrainer.permissions, 
                            communication: c,
                            messages: c ? editingTrainer.permissions?.messages : false,
                            ratings: c ? editingTrainer.permissions?.ratings : false,
                            surveys: c ? editingTrainer.permissions?.surveys : false,
                            events: c ? editingTrainer.permissions?.events : false,
                            evaluations: c ? editingTrainer.permissions?.evaluations : false,
                            goals: c ? editingTrainer.permissions?.goals : false
                          }
                        });
                      }}
                    />
                  </div>
                  <div className={`space-y-1.5 ml-2 ${!editingTrainer.permissions?.communication && editingTrainer.permissions?.communication !== undefined ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('common:messages')}</span>
                      <Switch
                        checked={editingTrainer.permissions?.messages !== false}
                        disabled={editingTrainer.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, messages: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('business:evaluations')}</span>
                      <Switch
                        checked={editingTrainer.permissions?.ratings || false}
                        disabled={editingTrainer.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, ratings: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("surveys:surveysLabel")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.surveys || false}
                        disabled={editingTrainer.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, surveys: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("events:eventsLabel")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.events || false}
                        disabled={editingTrainer.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, events: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("evaluations:memberEvaluations")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.evaluations || false}
                        disabled={editingTrainer.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, evaluations: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("evaluations:goalsAndBadges")}</span>
                      <Switch
                        checked={editingTrainer.permissions?.goals || false}
                        disabled={editingTrainer.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, goals: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Gelir Görme İzni */}
                <div className="border rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-navy" />
                      <span className="font-medium text-navy">{t("reports:viewRevenueInfo")}</span>
                      <span className="text-xs text-blue-600 bg-blue-100 px-1 rounded">{t("common:private")}</span>
                    </div>
                    <Switch
                      checked={editingTrainer.permissions?.can_view_revenue || false}
                      onCheckedChange={(c) => setEditingTrainer({
                        ...editingTrainer, 
                        permissions: {...editingTrainer.permissions, can_view_revenue: c}
                      })}
                    />
                  </div>
                  <p className="text-xs text-grey mt-2">{t("reports:ifPermOpenCanSeeIncome")}</p>
                </div>

                {/* Manuel Rezervasyon Tamamlama İzni - Sadece Yöneticilere Göster */}
                {editingTrainer.is_manager && (
                  <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-navy">Manuel Rezervasyon Tamamlama</span>
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </div>
                      <Switch
                        checked={editingTrainer.permissions?.manual_reservation_complete || false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, manual_reservation_complete: c}
                        })}
                      />
                    </div>
                    <p className="text-xs text-grey mt-2">{t("employees:ifPermOpenCanMarkComplete")}</p>
                  </div>
                )}

                {/* Manuel Rezervasyon Onaylama İzni - Sadece Yöneticilere Göster */}
                {editingTrainer.is_manager && (
                  <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-navy">Manuel Rezervasyon Onaylama</span>
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </div>
                      <Switch
                        checked={editingTrainer.permissions?.manual_reservation_approve || false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, manual_reservation_approve: c}
                        })}
                      />
                    </div>
                    <p className="text-xs text-grey mt-2">{t("employees:skipApprovalHint")}</p>
                  </div>
                )}

                {/* Geçmiş Tarihli Rezervasyon İzni - Sadece Yöneticilere Göster */}
                {editingTrainer.is_manager && (
                  <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-backdate_reservations-trainer-block">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-navy">{t("employees:backdateReservations", "Geçmiş Tarihli Rezervasyon")}</span>
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </div>
                      <Switch
                        checked={editingTrainer.permissions?.backdate_reservations !== false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, backdate_reservations: c}
                        })}
                        data-testid="perm-backdate_reservations-trainer"
                      />
                    </div>
                    <p className="text-xs text-grey mt-2">Açıksa: Yönetici, "Min. Önceden Rezervasyon" süresine bakmaksızın geçmiş tarihli rezervasyon oluşturabilir.</p>
                  </div>
                )}

                {/* Ders Açıklaması Düzenleme İzni - Sadece Yöneticilere Göster */}
                {editingTrainer.is_manager && (
                  <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-lesson_note_edit-trainer-block">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-navy">{t("employees:lessonNoteEdit", "Ders Açıklaması Düzenleme")}</span>
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </div>
                      <Switch
                        checked={editingTrainer.permissions?.lesson_note_edit !== false}
                        onCheckedChange={(c) => setEditingTrainer({
                          ...editingTrainer, 
                          permissions: {...editingTrainer.permissions, lesson_note_edit: c}
                        })}
                        data-testid="perm-lesson_note_edit-trainer"
                      />
                    </div>
                    <p className="text-xs text-grey mt-2">Açıksa: Tamamlanan derslere açıklama (not) ekleyebilir ve sonradan düzenleyebilir.</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" className="rounded-xl" onClick={() => setShowEditModal(false)}>{t('common:cancel')}</Button>
            <Button className="bg-navy hover:bg-navy/90 rounded-xl" onClick={handleUpdateTrainer} disabled={isSavingEdit}>
              {isSavingEdit ? "Kaydediliyor..." : t('common:save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTrainerId} onOpenChange={(open) => !open && setDeleteTrainerId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("common:antrenoruSil")}</DialogTitle>
            <DialogDescription>
              {t("common:deleteTrainerConfirm")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteTrainerId(null)}
              className="rounded-xl"
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteTrainer}
              className="rounded-xl"
            >
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bonus Payment Modal */}
      <Dialog open={showBonusModal} onOpenChange={(open) => {
        if (!open) {
          setShowBonusModal(false);
          setBonusTrainer(null);
        }
      }}>
        <DialogContent className="max-w-md w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-600" />
              {t("common:makeExtraPayment2")}
            </DialogTitle>
            <DialogDescription>
              {t("common:createExtraPaymentFor", { name: `${bonusTrainer?.name} ${bonusTrainer?.surname}` })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="bonus-amount">Tutar ({currencySymbol}) *</Label>
              <Input
                id="bonus-amount"
                type="number"
                min="0"
                step="1"
                value={bonusForm.amount}
                onChange={(e) => setBonusForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="1000"
                className="rounded-xl"
                data-testid="bonus-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="bonus-date">{t("common:paymentDate")}</Label>
              <DatePickerTR
                value={bonusForm.payment_date ? new Date(bonusForm.payment_date) : null}
                onChange={(date) => setBonusForm(prev => ({ ...prev, payment_date: date ? formatDateToLocal(date) : "" }))}
                placeholder={t('common:selectDate')}
                data-testid="bonus-date-input"
              />
            </div>
            <div>
              <Label htmlFor="bonus-method">{t('payments:paymentMethod')}</Label>
              <Select 
                value={bonusForm.payment_method} 
                onValueChange={(val) => setBonusForm(prev => ({ ...prev, payment_method: val }))}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                  <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bonus-reason">{t("common:descriptionReason")}</Label>
              <Input
                id="bonus-reason"
                type="text"
                value={bonusForm.reason}
                onChange={(e) => setBonusForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Performans primi, bayram ikramiyesi vb."
                className="rounded-xl"
                data-testid="bonus-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowBonusModal(false);
                setBonusTrainer(null);
              }}
              className="rounded-xl"
            >
              {t("common:cancel")}
            </Button>
            <Button
              onClick={handleBonusPayment}
              className="rounded-xl bg-amber-600 hover:bg-amber-700"
              data-testid="bonus-submit-btn"
            >
              <Gift className="w-4 h-4 mr-2" />
              {t("common:makePayment")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
